import { initializeApp } from 'firebase/app';
import { getFirestore, doc, setDoc, getDoc, serverTimestamp, getDocs, query, where, collection, getDocFromServer, onSnapshot } from 'firebase/firestore';
import { getAuth } from 'firebase/auth';
import firebaseConfig from '../firebase-applet-config.json';
import { addBreadcrumb, captureException, captureMessage } from './lib/sentry.ts';

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const db = getFirestore(app, firebaseConfig.firestoreDatabaseId);
const auth = getAuth(app);

// Test connection
const testConnection = async () => {
  try {
    await getDocFromServer(doc(db, 'test', 'connection'));
  } catch (error) {
    if (error instanceof Error && error.message.includes('the client is offline')) {
      console.error("Please check your Firebase configuration.");
    }
  }
};
testConnection();

// Error handler
enum OperationType {
  CREATE = 'create',
  UPDATE = 'update',
  DELETE = 'delete',
  LIST = 'list',
  GET = 'get',
  WRITE = 'write',
}

interface FirestoreErrorInfo {
  error: string;
  operationType: OperationType;
  path: string | null;
  authInfo: {
    userId?: string | null;
    email?: string | null;
    emailVerified?: boolean | null;
    isAnonymous?: boolean | null;
  }
}

function handleFirestoreError(error: unknown, operationType: OperationType, path: string | null) {
  const errInfo: FirestoreErrorInfo = {
    error: error instanceof Error ? error.message : String(error),
    authInfo: {
      userId: auth.currentUser?.uid,
      email: auth.currentUser?.email,
      emailVerified: auth.currentUser?.emailVerified,
      isAnonymous: auth.currentUser?.isAnonymous,
    },
    operationType,
    path
  };
  console.error('Firestore Error: ', JSON.stringify(errInfo));
  addBreadcrumb({
    category: 'firestore',
    message: `Firestore operation failed: ${operationType} on ${path}`,
    level: 'error',
    data: errInfo
  });
  captureMessage(`Firestore write failed`, {
    level: 'error',
    extra: errInfo
  });
  captureException(error, {
    extra: errInfo
  });
  throw new Error(JSON.stringify(errInfo));
}

/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import React, { useState, useEffect, useMemo, createContext, useContext } from "react";
import { motion, AnimatePresence } from "motion/react";
import { translations, Language } from "./translations";
import { PayPalCheckoutStep } from "./components/PayPalCheckoutStep";
import { 
  Instagram, 
  Facebook, 
  MapPin, 
  CheckCircle2, 
  ArrowRight, 
  X,
  Magnet,
  ChevronRight,
  BarChart3,
  Target,
  Link,
  Globe,
  Zap,
  Youtube,
  Mail,
  Music,
  ShieldCheck,
  Handshake,
  Briefcase,
  Shield,
  Star,
  Heart,
  Eye,
  Info,
  Lock,
  CreditCard
} from "lucide-react";

// --- Context ---

const LanguageContext = createContext<{
  lang: Language;
  setLang: (lang: Language) => void;
  t: typeof translations.en;
} | null>(null);

const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error("useTranslation must be used within LanguageProvider");
  return context;
};

// --- Components ---

const Navbar = () => {
  const { lang, setLang, t } = useTranslation();

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 px-3 py-0 sm:px-6 sm:py-2">
      <div className="max-w-7xl mx-auto flex justify-between items-center glass-navbar rounded-xl px-3 h-[50px] sm:h-[60px] relative border-white/30 sm:border-white/20">
        <div className="flex items-center gap-[12px] ml-[12px] z-10 no-rtl">
          <img 
            src="https://i.postimg.cc/zf1RDhgw/56868A94-6A20-494A-A652-3F264D8C20B5-removebg-preview(2).png" 
            alt="MediaMagnet Logo" 
            className="h-[88px] w-auto object-contain block translate-y-2"
            referrerPolicy="no-referrer"
          />
          <span className="font-black text-lg sm:text-xl tracking-tighter sm:tracking-tight">MediaMagnet</span>
        </div>
        {/* Removed platforms and process labels from the nav dock as requested */}
        
        <div className="flex items-center gap-4 z-10">
          <div className="relative group">
            {/* Bloom / Glow Effect (Behind) */}
            <motion.div
              animate={{ 
                opacity: [0.1, 0.4, 0.1]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="absolute inset-[-2px] rounded-xl blur-[8px] z-0"
              style={{
                background: 'conic-gradient(from 0deg, #ff0000, #ff7f00, #ffff00, #00ff00, #00ffff, #0000ff, #8b00ff, #ff0000)',
              }}
            />

            {/* Fixed RGB Ring (The Edge) */}
            <motion.div
              animate={{ 
                opacity: [0.4, 0.8, 0.4]
              }}
              transition={{ 
                duration: 4, 
                repeat: Infinity, 
                ease: "easeInOut" 
              }}
              className="absolute inset-0 rounded-xl p-[1px] z-0 overflow-hidden"
              style={{
                background: 'conic-gradient(from 0deg, #ff0000, #ff7f00, #ffff00, #00ff00, #00ffff, #0000ff, #8b00ff, #ff0000)',
              }}
            >
              {/* Solid Black Center Mask */}
              <div className="w-full h-full rounded-xl bg-black" />
            </motion.div>
            
            <div className="relative z-10 flex items-center gap-2 bg-black/90 backdrop-blur-md p-0 rounded-xl border border-white/30 sm:border-white/10 no-rtl">
              <button 
                onClick={() => setLang('en')}
                className={`w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg transition-all overflow-hidden ${lang === 'en' ? 'bg-white/20 shadow-lg scale-110' : 'hover:bg-white/5'}`}
                title="English"
              >
                <img 
                  src="https://flagcdn.com/gb.svg" 
                  alt="UK Flag" 
                  className="w-5 h-5 sm:w-7 sm:h-7 object-contain rounded-sm"
                  referrerPolicy="no-referrer"
                />
              </button>
              <button 
                onClick={() => setLang('de')}
                className={`w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg transition-all overflow-hidden ${lang === 'de' ? 'bg-white/20 shadow-lg scale-110' : 'hover:bg-white/5'}`}
                title="Deutsch"
              >
                <img 
                  src="https://flagcdn.com/de.svg" 
                  alt="German Flag" 
                  className="w-5 h-5 sm:w-7 sm:h-7 object-contain rounded-sm"
                  referrerPolicy="no-referrer"
                />
              </button>
              <button 
                onClick={() => setLang('ar')}
                className={`w-7 h-7 sm:w-10 sm:h-10 flex items-center justify-center rounded-lg transition-all overflow-hidden ${lang === 'ar' ? 'bg-white/20 shadow-lg scale-110' : 'hover:bg-white/5'}`}
                title="العربية"
              >
                <img 
                  src="https://flagcdn.com/sa.svg" 
                  alt="Saudi Arabia Flag" 
                  className="w-5 h-5 sm:w-7 sm:h-7 object-contain rounded-sm"
                  referrerPolicy="no-referrer"
                />
              </button>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

const FloatingScreenshot = ({ src, delay, position, isPlain }: { src: string, delay: number, position: string, isPlain?: boolean }) => {
  const posClasses = {
    'top-left': 'top-[12%] -left-[15%] sm:top-[10%] sm:left-[5%] -rotate-6',
    'top-right': 'top-[15%] right-[5%] rotate-6',
    'bottom-left': 'bottom-[15%] -left-[28%] sm:left-[8%] rotate-3',
    'bottom-right': 'bottom-[5%] right-[-15%] sm:bottom-[10%] sm:right-[8%] -rotate-3',
  }[position as 'top-left' | 'top-right' | 'bottom-left' | 'bottom-right'];

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8, y: 20 }}
      animate={{ 
        opacity: [0, 1, 0.9, 1, 0],
        scale: [0.9, 1.02, 0.98, 1.02, 0.9],
        y: [0, -15, -30, -45, -60],
        x: position.includes('left') ? [0, 5, 10, 15, 20] : [0, -5, -10, -15, -20]
      }}
      transition={{ 
        duration: 5,
        repeat: Infinity,
        delay,
        ease: "easeInOut",
        times: [0, 0.1, 0.45, 0.8, 1]
      }}
      className={`absolute z-0 ${position === 'bottom-left' ? 'w-56 sm:w-72' : 'w-40 sm:w-72'} ${isPlain ? '' : 'rounded-2xl overflow-hidden shadow-2xl border border-white/20'} ${posClasses}`}
    >
      <img 
        src={src} 
        alt="Social Proof" 
        className="w-full h-auto object-cover"
        referrerPolicy="no-referrer"
      />
      {!isPlain && <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent pointer-events-none" />}
    </motion.div>
  );
};

const Hero = ({ onStartFunnel, onShowInfo }: { onStartFunnel: () => void, onShowInfo: () => void }) => {
  const { t } = useTranslation();
  
  const images = [
    "/regenerated_image_1777400433332.jpg",
    "https://i.postimg.cc/c46QzB8r/IMG-2440.jpg",
    "https://i.postimg.cc/Kc0PQH4S/IMG_2446.jpg",
    "https://i.postimg.cc/hvmmKWW2/phone-live.png"
  ];

  return (
    <section className="relative min-h-screen flex flex-col items-center justify-start pt-24 sm:pt-48 pb-4 sm:pb-16 px-6 overflow-hidden bg-[#050505]">
      {/* Intro Emoji Animation */}
      <motion.div
        initial={{ opacity: 0, scale: 0.5, y: 50 }}
        animate={{ 
          opacity: [0, 1, 1, 0],
          scale: [0.5, 1.2, 1.2, 0.5],
          y: [50, 0, 0, 50]
        }}
        transition={{ 
          duration: typeof window !== 'undefined' && window.innerWidth < 640 ? 3 : 2,
          times: [0, 0.2, 0.8, 1],
          ease: "easeInOut"
        }}
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-0 pointer-events-none"
      >
        <img 
          src="https://i.postimg.cc/HxHdC13q/WINK-removebg-preview.png" 
          alt="Winking Emoji" 
          className="w-64 h-64 md:w-[500px] md:h-[500px] object-contain opacity-50"
          referrerPolicy="no-referrer"
        />
      </motion.div>

      {/* Dark Gradient Overlay */}
      <div className="absolute inset-0 z-[1] bg-gradient-to-b from-black via-transparent to-black opacity-80 pointer-events-none" />
      <div className="absolute inset-0 z-[1] bg-[radial-gradient(circle_at_center,transparent_0%,rgba(0,0,0,0.4)_100%)] pointer-events-none" />

      {/* Floating Screenshots around edges */}
      <FloatingScreenshot src={images[0]} delay={0} position="top-left" />
      <FloatingScreenshot src={images[2]} delay={0.4} position="top-right" />
      <FloatingScreenshot src={images[3]} delay={0.8} position="bottom-left" isPlain />
      <FloatingScreenshot src={images[1]} delay={1.2} position="bottom-right" />
      
      {/* Background Glows */}
      <div className="absolute top-1/4 -left-20 w-96 h-96 bg-blue-600/10 rounded-full blur-[120px] z-0" />
      <div className="absolute bottom-1/4 -right-20 w-96 h-96 bg-blue-900/10 rounded-full blur-[120px] z-0" />
      
      <div className="relative z-10 max-w-5xl mx-auto text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
        >
          <h1 className="text-5xl md:text-8xl font-black mb-4 sm:mb-12 tracking-tight leading-tight text-gradient whitespace-pre-line">
            {t.hero.title}
          </h1>
          <p className="text-xl md:text-2xl text-white/40 sm:text-white/60 mb-8 sm:mb-16 max-w-2xl mx-auto leading-relaxed translate-y-1">
            {t.hero.subtitle}
          </p>
          
          <div className="flex flex-col items-center justify-center gap-12 sm:gap-20">
            <button 
              onClick={onShowInfo}
              className="relative w-24 h-24 sm:w-44 sm:h-44 rounded-full flex items-center justify-center group shrink-0 transition-transform hover:scale-105 active:scale-95 mt-8 sm:mt-0"
            >
              {/* Bloom / Glow Effect (Behind) */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                className="absolute inset-[-2px] sm:inset-[-4px] rounded-full blur-[8px] sm:blur-[12px] opacity-40 group-hover:opacity-60 transition-opacity"
                style={{
                  background: 'conic-gradient(from 0deg, #ff0000, #ff7f00, #ffff00, #00ff00, #00ffff, #0000ff, #8b00ff, #ff0000)',
                }}
              />

              {/* Animated RGB Ring (The Edge) */}
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                className="absolute inset-0 rounded-full p-[1.5px] sm:p-[2px]"
                style={{
                  background: 'conic-gradient(from 0deg, #ff0000, #ff7f00, #ffff00, #00ff00, #00ffff, #0000ff, #8b00ff, #ff0000)',
                }}
              >
                {/* Solid Black Center */}
                <div className="w-full h-full rounded-full bg-black" />
              </motion.div>

              {/* Content */}
              <div className="relative z-10 flex flex-col items-center justify-center text-center px-4">
                <span className="text-[8px] sm:text-xs font-bold uppercase tracking-[0.3em] mb-1 text-white/40 group-hover:text-white/70 transition-colors">
                  {t.hero.info}
                </span>
                <span className="text-[10px] sm:text-lg font-black leading-tight text-white tracking-tight">
                  {t.hero.tellMeMore}
                </span>
              </div>
            </button>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

const BrandLogo = ({ platform }: { platform: string }) => {
  const { lang } = useTranslation();
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);
  
  const instagramGradient = (
    <linearGradient id="insta-grad" x1="0%" y1="100%" x2="100%" y2="0%">
      <stop offset="0%" stopColor="#f09433" />
      <stop offset="25%" stopColor="#e6683c" />
      <stop offset="50%" stopColor="#dc2743" />
      <stop offset="75%" stopColor="#cc2366" />
      <stop offset="100%" stopColor="#bc1888" />
    </linearGradient>
  );

  const googleColors = {
    blue: "#4285F4",
    red: "#EA4335",
    yellow: "#FBBC05",
    green: "#34A853"
  };

  const logos: Record<string, React.ReactNode> = {
    instagram: (
      <svg viewBox="0 0 24 24" className="w-full h-full">
        <defs>{instagramGradient}</defs>
        <path 
          fill="url(#insta-grad)" 
          d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" 
        />
      </svg>
    ),
    facebook: (
      <svg viewBox="0 0 24 24" className="w-full h-full" fill="#1877F2">
        <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
      </svg>
    ),
    google: (
      <img 
        src="https://i.postimg.cc/66bJMZsM/IMG-2499.png" 
        alt="Google Maps" 
        className="w-full h-full object-contain"
        referrerPolicy="no-referrer"
      />
    ),
    tiktok: (
      <svg viewBox="0 0 24 24" className="w-full h-full">
        <path fill="#00f2ea" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1 .05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" transform="translate(-0.6, -0.6)" />
        <path fill="#ff0050" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1 .05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" transform="translate(0.6, 0.6)" />
        <path fill="white" d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1 .05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1.04-.1z" />
      </svg>
    ),
    youtube: (
      <svg viewBox="0 0 24 24" className="w-full h-full" fill="#FF0000">
        <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" />
      </svg>
    ),
    telegram: (
      <svg viewBox="0 0 24 24" className="w-full h-full" fill="#0088cc">
        <path d="M12 24c6.627 0 12-5.373 12-12S18.627 0 12 0 0 5.373 0 12s5.373 12 12 12z" />
        <path fill="#FFF" d="M5.491 11.721l13.116-5.056c.607-.227 1.139.141.942.897l-2.233 10.531c-.164.739-.6.921-1.221.574l-3.411-2.511-1.644 1.583c-.183.183-.341.341-.682.341l.241-3.447 6.273-5.659c.273-.241-.059-.379-.418-.141l-7.756 4.885-3.344-1.045c-.727-.227-.738-.727.151-1.074z" />
      </svg>
    )
  };

  return (
    <motion.div
      animate={isMobile ? {} : {
        scale: [1, 1.05, 1],
        filter: [
          "drop-shadow(0 0 0px rgba(255,255,255,0))",
          "drop-shadow(0 0 15px rgba(255,255,255,0.3))",
          "drop-shadow(0 0 0px rgba(255,255,255,0))"
        ]
      }}
      transition={{
        duration: 3,
        repeat: Infinity,
        ease: "easeInOut"
      }}
      className="w-full h-full flex items-center justify-center"
    >
      {logos[platform] || null}
    </motion.div>
  );
};

const PlatformCard = ({ platform, color, title, onStartFunnel, isComingSoon, fullImage, className = "" }: any) => {
  const { t } = useTranslation();
  const getBrandGlow = () => {
    switch (platform) {
      case 'instagram':
        return "bg-gradient-to-tr from-[#f09433] via-[#dc2743] to-[#bc1888]";
      case 'facebook':
        return "bg-[#1877F2]";
      case 'google':
        return "bg-gradient-to-r from-[#4285F4] via-[#EA4335] via-[#FBBC05] to-[#34A853]";
      case 'tiktok':
        return "bg-gradient-to-r from-[#00f2ea] via-black to-[#ff0050]";
      case 'youtube':
        return "bg-[#FF0000]";
      case 'telegram':
        return "bg-[#229ED9]";
      default:
        return `bg-${color}-500`;
    }
  };

  return (
    <motion.div
      whileHover={isComingSoon ? {} : { y: -8, scale: 1.02 }}
      onClick={() => !isComingSoon && onStartFunnel(title)}
      className={`glass aspect-square rounded-2xl sm:rounded-[2.5rem] flex flex-col items-center justify-center text-center group relative overflow-hidden transition-all border border-white/5 ${isComingSoon ? 'cursor-not-allowed opacity-90' : 'cursor-pointer hover:bg-white/5 hover:border-white/10'} ${className}`}
    >
      {fullImage && (
        <img 
          src={fullImage} 
          className="absolute inset-0 w-full h-full object-cover z-0" 
          alt={title}
          referrerPolicy="no-referrer"
        />
      )}

      {/* Lock Icon for Coming Soon */}
      {isComingSoon && (
        <div 
          className="absolute top-4 right-4 z-20 bg-black/40 p-1.5 rounded-full backdrop-blur-md border border-white/10"
          style={
            platform === 'youtube' 
              ? { paddingLeft: '5px', marginLeft: '-7px', marginRight: '-8px', marginBottom: '5px' }
              : platform === 'telegram'
              ? { marginRight: '-10px' }
              : undefined
          }
        >
          <Lock size={16} className="text-white" />
        </div>
      )}

      {/* Animated Brand Glow */}
      {!fullImage && (
        <motion.div
          animate={{
            opacity: [0.15, 0.4, 0.15],
            scale: [1, 1.15, 1],
          }}
          transition={{
            duration: 5,
            repeat: Infinity,
            ease: "easeInOut",
            times: [0, 0.35, 1]
          }}
          className={`absolute inset-0 ${getBrandGlow()} blur-3xl opacity-20`}
        />
      )}

      {/* Hover Spotlight */}
      <div className={`absolute top-0 right-0 w-32 h-32 ${getBrandGlow()} blur-3xl -mr-16 -mt-16 opacity-0 group-hover:opacity-40 transition-all duration-500`} />
      
      {!fullImage && (
        <>
          <div className={`relative z-10 w-12 h-12 sm:w-20 sm:h-20 flex items-center justify-center transition-transform duration-500 mb-2 sm:mb-4 ${!isComingSoon ? 'group-hover:scale-110' : ''}`}>
            <BrandLogo platform={platform} />
          </div>

          <div className="relative z-10 px-1 w-full">
            <h3 className="text-[12px] sm:text-lg font-bold text-white/90 group-hover:text-white transition-colors truncate mb-1">
              {title}
            </h3>
            {isComingSoon && (
              <div className="flex items-center justify-center gap-1 mt-1 sm:mt-2">
                <span className="text-[10px] sm:text-xs font-black uppercase tracking-[0.2em] text-white bg-transparent px-3 py-1 rounded-full whitespace-nowrap border border-white/40">
                  {t.modals.funnel.buttons.comingSoon}
                </span>
              </div>
            )}
          </div>
        </>
      )}
      
      {/* Subtle LED Border Effect */}
      <motion.div
        animate={{
          opacity: [0.3, 0.8, 0.3],
        }}
        transition={{
          duration: 4,
          repeat: Infinity,
          ease: "easeInOut",
          times: [0, 0.35, 1]
        }}
        className={`absolute inset-0 rounded-2xl sm:rounded-[2.5rem] border-2 border-transparent bg-gradient-to-r ${getBrandGlow()} [mask-image:linear-gradient(white,white)] [mask-clip:padding-box] opacity-40`}
        style={{ WebkitMaskImage: 'linear-gradient(#fff 0 0), linear-gradient(#fff 0 0)', WebkitMaskClip: 'content-box, border-box', WebkitMaskComposite: 'xor', maskComposite: 'exclude' }}
      />
    </motion.div>
  );
};

const Platforms = ({ onStartFunnel }: { onStartFunnel: (platform: string) => void }) => {
  const { t, lang } = useTranslation();
  
  const googleMapsImage = lang === 'ar' 
    ? "https://i.postimg.cc/cCKrMFW8/Arabic-manipu.png"
    : lang === 'de'
    ? "https://i.postimg.cc/LXQJ1rcH/mani-german.png"
    : "https://i.postimg.cc/KjYnX720/mani-english.jpg";

  return (
    <section id="platforms" className={`pt-0 pb-16 px-2 sm:px-6 relative ${lang === 'en' ? '-mt-20' : '-mt-24'} sm:mt-0 z-10`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-4 -mt-32 sm:mt-0">
          <h2 className={`text-2xl md:text-3xl font-black leading-relaxed md:leading-normal opacity-0 md:opacity-100 text-white`}>
            {t.platforms.title}
          </h2>
        </div>
        <div className={`grid grid-cols-[0.98fr_1.04fr_0.98fr] sm:grid-cols-3 gap-x-1.5 gap-y-12 sm:gap-6 -mt-6 sm:mt-0`} dir="ltr">
          <PlatformCard
            color="pink"
            platform="instagram"
            title="Instagram"
            onStartFunnel={onStartFunnel}
            className="translate-y-2 h-[105%] aspect-auto sm:aspect-square sm:translate-y-0 sm:h-auto"
          />
          {/* Mobile Position 2: TikTok */}
          <div className="block md:hidden">
            <PlatformCard
              color="purple"
              platform="tiktok"
              title="TikTok"
              onStartFunnel={onStartFunnel}
              className="translate-y-2 h-[105%] aspect-auto"
            />
          </div>
          {/* Desktop Position 2: TikTok (Desktop Only Switch) */}
          <div className="hidden md:block">
            <PlatformCard
              color="purple"
              platform="tiktok"
              title="TikTok"
              onStartFunnel={onStartFunnel}
            />
          </div>
          <PlatformCard
            color="blue"
            platform="facebook"
            title="Facebook"
            onStartFunnel={onStartFunnel}
            className="translate-y-2 h-[105%] aspect-auto sm:aspect-square sm:translate-y-0 sm:h-auto"
          />
          <PlatformCard
            color="red"
            platform="youtube"
            title="YouTube"
            isComingSoon={true}
            onStartFunnel={onStartFunnel}
            className="translate-y-2 h-[105%] aspect-auto sm:aspect-square sm:translate-y-0 sm:h-auto"
          />
          {/* Mobile Position 5: Google Maps */}
          <div className="block md:hidden">
            <PlatformCard
              color="green"
              platform="google"
              title="Google Maps"
              onStartFunnel={onStartFunnel}
              fullImage={googleMapsImage}
              className="translate-y-2 h-[105%] aspect-auto"
            />
          </div>
          {/* Desktop Position 5: Google Maps */}
          <div className="hidden md:block">
            <PlatformCard
              color="green"
              platform="google"
              title="Google Maps"
              onStartFunnel={onStartFunnel}
              fullImage={googleMapsImage}
            />
          </div>
          <PlatformCard
            color="sky"
            platform="telegram"
            title="Telegram"
            isComingSoon={true}
            onStartFunnel={onStartFunnel}
            className="translate-y-2 h-[105%] aspect-auto sm:aspect-square sm:translate-y-0 sm:h-auto"
          />
        </div>
      </div>
    </section>
  );
};

const InfoModal = ({ isOpen, onClose }: { isOpen: boolean, onClose: () => void }) => {
  const { t, lang } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Switch image based on current language
  const imageUrl = lang === 'ar'
    ? "https://i.postimg.cc/HLJ7Tk8J/arabic-transla.png"
    : lang === 'en'
    ? "https://i.postimg.cc/q7Lp56sy/IMG-2747.jpg"
    : "https://i.postimg.cc/1RTs2Qqf/IMG-2748.jpg";

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/90 backdrop-blur-2xl"
          />
          
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="relative w-full max-w-7xl bg-[#f8f9fa] rounded-2xl sm:rounded-[2.5rem] shadow-[0_0_50px_rgba(0,0,0,0.5)] overflow-hidden flex flex-col"
          >
            <button 
              onClick={onClose}
              className="absolute top-4 right-4 z-[110] w-12 h-12 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80 transition-all shadow-xl backdrop-blur-md border border-white/10"
            >
              <X size={28} />
            </button>

            <div className="relative flex-1 overflow-auto p-1 sm:p-4 flex items-center justify-center min-h-[400px]">
              {loading && !error && (
                <div className="absolute inset-0 flex items-center justify-center text-black/50">
                  <div className="flex flex-col items-center gap-4">
                    <div className="w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
                    <p className="text-xl font-bold">{t.modals.info.loading}</p>
                  </div>
                </div>
              )}
              
              {error && (
                <div className="absolute inset-0 flex items-center justify-center text-red-500 p-8 text-center">
                  <div className="flex flex-col items-center gap-4">
                    <p className="text-xl font-bold">{t.modals.info.error}</p>
                    <button 
                      onClick={() => { setError(false); setLoading(true); }}
                      className="px-6 py-2 bg-blue-600 text-white rounded-full hover:bg-blue-500 transition-colors"
                    >
                      {t.modals.info.retry}
                    </button>
                  </div>
                </div>
              )}

              <img 
                src={imageUrl}
                alt="Wie MediaMagnet ihr Leben Verändert"
                className={`max-w-full max-h-[92vh] object-contain shadow-lg rounded-lg transition-opacity duration-500 scale-105 sm:scale-100 ${loading || error ? 'opacity-0' : 'opacity-100'}`}
                referrerPolicy="no-referrer"
                onLoad={() => {
                  setLoading(false);
                  setError(false);
                }}
                onError={(e) => {
                  console.error("Failed to load image:", imageUrl);
                  setLoading(false);
                  setError(true);
                }}
              />
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};



const OrderTracker = () => {
  const { t, lang } = useTranslation();
  const [orderId, setOrderId] = useState("");
  const [status, setStatus] = useState<'idle' | 'processing' | 'completed' | 'invalid'>('idle');
  const [loading, setLoading] = useState(false);
  const [activeCode, setActiveCode] = useState<string | null>(null);

  useEffect(() => {
    if (!activeCode) return;

    // Start real-time listener for the specific order identification
    const q = query(collection(db, "Orders"), where("trackingCode", "==", activeCode));
    
    setLoading(true);
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      setLoading(false);
      if (!querySnapshot.empty) {
        const orderData = querySnapshot.docs[0].data();
        const dbStatus = String(orderData.status || "").toLowerCase();
        
        // Accept multiple variations of "DONE"
        if (dbStatus === 'done' || dbStatus === 'completed' || dbStatus === 'd' || dbStatus === 'fertig' || dbStatus === 'fertiggestellt') {
          setStatus('completed');
        } else if (dbStatus === 'processing' || dbStatus === 'p' || dbStatus === 'in bearbeitung' || dbStatus === 'active') {
          setStatus('processing');
        } else {
          setStatus('processing');
        }
      } else {
        setStatus('invalid');
      }
    }, (error) => {
      console.error("Tracking listener error:", error);
      handleFirestoreError(error, OperationType.LIST, `Orders`);
      setStatus('invalid');
      setLoading(false);
    });

    return () => unsubscribe();
  }, [activeCode]);

  const handleCheck = () => {
    if (!orderId.trim()) {
      setStatus('invalid');
      return;
    }
    
    const code = orderId.trim().toUpperCase();
    setActiveCode(code);
    setStatus('idle');
  };

  const tracking = (t as any).tracking;

  return (
    <section id="process" className={`pb-20 sm:py-20 px-6 ${lang === 'en' ? 'pt-6' : 'pt-2'}`}>
      <div className="max-w-5xl mx-auto text-center">
        <div className="max-w-xl mx-auto mb-12">
          <h2 className="text-4xl md:text-6xl font-black mb-4">{tracking.title}</h2>
          <p className="text-white/60 text-lg">{tracking.subtitle}</p>
        </div>
        
        <div className="max-w-xl mx-auto space-y-6 mb-16">
          <div className="relative group">
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder={tracking.placeholder}
              className="w-full bg-white/5 border border-white/10 rounded-2xl px-6 py-4 text-white placeholder:text-white/20 focus:outline-none focus:border-blue-500/50 transition-all text-center text-lg tracking-widest font-mono group-hover:border-white/20"
            />
          </div>
          
          <button
            onClick={handleCheck}
            disabled={loading}
            className="w-full bg-blue-600 hover:bg-blue-500 text-white py-4 rounded-2xl font-bold transition-all hover:scale-[1.02] active:scale-95 shadow-xl glow-blue disabled:opacity-50"
          >
            {loading ? (
              <div className="flex items-center justify-center gap-2">
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Checking...</span>
              </div>
            ) : tracking.button}
          </button>
        </div>



        <div className="max-w-2xl mx-auto">
          <AnimatePresence>
            {status === 'invalid' && (
              <motion.div
                key="invalid"
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="mt-6 text-red-400 font-bold bg-red-400/10 px-6 py-3 rounded-xl border border-red-400/20 inline-block"
              >
                {tracking.invalid}
              </motion.div>
            )}
          </AnimatePresence>

          <div className="mt-12 min-h-[200px] flex items-center justify-center">
            <AnimatePresence mode="wait">
              {status === 'processing' && (
                <motion.div
                  key="processing"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="bg-white/[0.03] border border-white/10 rounded-[2.5rem] p-8 sm:p-10 backdrop-blur-md text-left w-full shadow-2xl relative overflow-hidden"
                >
                  <div className="relative z-10 space-y-8">
                    {/* Title Area */}
                    <div className="flex items-center gap-3">
                      <h3 className="text-white text-lg sm:text-xl font-bold tracking-tight">
                        {tracking.validProcessing.split('\n\n')[0]}
                      </h3>
                      <div className="flex-shrink-0 w-6 h-6 bg-[#22c55e] rounded-full flex items-center justify-center shadow-[0_0_15px_rgba(34,197,94,0.4)]">
                        <CheckCircle2 size={14} className="text-white" strokeWidth={4} />
                      </div>
                    </div>

                    {/* Content Area */}
                    <div className="space-y-6 text-[#d1d5db] leading-relaxed text-sm sm:text-base font-normal">
                      {tracking.validProcessing.split('\n\n').slice(1).map((para: string, idx: number) => (
                        <p key={idx}>{para}</p>
                      ))}
                    </div>
                  </div>
                  
                  {/* Subtle gradient glow */}
                  <div className="absolute top-0 right-0 w-64 h-64 bg-blue-500/5 blur-[100px] -mr-32 -mt-32 pointer-events-none" />
                </motion.div>
              )}

              {status === 'completed' && (
                <motion.div
                  key="completed"
                  initial={{ opacity: 0, scale: 0.8, filter: "blur(10px)" }}
                  animate={{ 
                    opacity: 1, 
                    scale: 1, 
                    filter: "blur(0px)",
                  }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  className="flex flex-col items-center gap-8 py-4"
                >
                  <div className="relative">
                    <motion.div
                      animate={{ 
                        scale: [1, 1.2, 1],
                        opacity: [0.3, 0.6, 0.3]
                      }}
                      transition={{ duration: 2, repeat: Infinity }}
                      className="absolute inset-0 bg-green-500 rounded-full blur-2xl"
                    />
                    <div className="relative w-32 h-32 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center shadow-[0_0_60px_rgba(34,197,94,0.3)] border border-green-500/30">
                      <CheckCircle2 size={80} className="animate-in zoom-in duration-700" />
                    </div>
                  </div>
                  
                  <motion.div
                    initial={{ y: 20, opacity: 0 }}
                    animate={{ y: 0, opacity: 1 }}
                    transition={{ delay: 0.3 }}
                    className="text-center space-y-2"
                  >
                    <p className="text-3xl font-black text-white drop-shadow-lg">
                      {tracking.completed}
                    </p>
                    <div className="h-1 w-24 bg-gradient-to-r from-transparent via-green-500 to-transparent mx-auto rounded-full" />
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </section>
  );
};

const FloatingHearts = ({ count = 12 }: { count?: number }) => {
  const hearts = useMemo(() => 
    Array.from({ length: count }).map((_, i) => {
      const isLeft = i < count / 2;
      return {
        id: i,
        left: isLeft ? Math.random() * 15 + 5 : 80 + Math.random() * 15,
        size: 24, // Consistent size
        duration: 3, // Steady speed (increased by 40% from 5s)
        delay: Math.random() * 3,
      };
    }), [count]
  );

  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
      {hearts.map((heart) => (
        <motion.img
          key={heart.id}
          src="https://i.postimg.cc/RVfSCzqH/insta-heart-removebg-preview.png"
          initial={{ y: 60, x: 0 }}
          animate={{ 
            y: -240, // Increased distance for full coverage
          }}
          transition={{ 
            duration: heart.duration, 
            delay: heart.delay,
            repeat: Infinity,
            ease: "linear"
          }}
          style={{ 
            position: 'absolute', 
            bottom: 0,
            left: `${heart.left}%`,
            width: heart.size,
            height: heart.size,
          }}
          referrerPolicy="no-referrer"
        />
      ))}
    </div>
  );
};

const SummaryDisplay = ({ formData, lang }: { formData: any, lang: string }) => {
  const goalParts = formData.goal ? formData.goal.split(' - ') : [];
  const title = formData.selectedServiceName || goalParts[0] || "";
  const price = goalParts.length > 1 ? goalParts.pop() : null;

  const isHighSpeed = title.toLowerCase().includes('view') || 
                     title.toLowerCase().includes('aufrufe') || 
                     title.toLowerCase().includes('مشاهدات') ||
                     title.toLowerCase().includes('speed');

  return (
    <>
      <motion.div 
        key={formData.selectedQuantity}
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        className="mb-2 lg:mb-4"
      >
        <span className="text-5xl sm:text-8xl font-black flex flex-col leading-none">
          <span className="bg-gradient-to-br from-white to-white/40 bg-clip-text text-transparent">
            {formData.selectedQuantity || "10"}
          </span>
        </span>
      </motion.div>

      <div className="relative z-10 w-full flex flex-col items-center lg:items-end">
        <h3 className="text-2xl sm:text-4xl font-black text-white mb-2 leading-tight">
          {title}
        </h3>
        <div className="flex flex-col items-center lg:items-end gap-2 sm:gap-3">
          {isHighSpeed && (
            <div className="flex items-center gap-2 text-blue-400 font-bold uppercase tracking-[0.2em] text-[10px] sm:text-xs">
              <Zap size={14} />
              <span>{formData.platform} {lang === 'de' ? 'Hochgeschwindigkeit' : 'High Speed'}</span>
            </div>
          )}
          {price && (
            <div className={`text-white/40 text-[9px] sm:text-[11px] font-bold uppercase tracking-[0.15em] mt-1 ${formData.platform?.toLowerCase() === 'facebook' ? 'block' : 'hidden sm:block'}`}>
              {price}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

const FacebookLikesEmoji = () => {
  const images = [
    "https://i.postimg.cc/QxWthqxB/Gemini-Generateddada-Image-1j0vym1j0vym1j0v-removebg-preview-1-removebg-preview(1).png",
    "https://i.postimg.cc/V6CfmfmX/Screenshot-2026-05-0dd6-001107-removebg-preview(3).png",
    "https://i.postimg.cc/RCgCcDqm/Untitlsed-removebg-preview.png",
    "https://i.postimg.cc/76gVFzGc/Unthhitled-removebg-preview.png",
    "https://i.postimg.cc/J7FV7VGF/22Untitled-removebg-preview.png"
  ];
  
  const [index, setIndex] = React.useState(0);

  React.useEffect(() => {
    const timer = setInterval(() => {
      setIndex((prev) => (prev + 1) % images.length);
    }, 2500); // Slightly slower for better visibility
    return () => clearInterval(timer);
  }, [images.length]);

  return (
    <div className="relative w-6 h-6 sm:w-9 sm:h-9 ml-1 inline-block vertical-middle">
      <AnimatePresence mode="wait">
        <motion.img
          key={images[index]}
          src={images[index]}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.8 }}
          className="absolute inset-0 w-full h-full object-contain"
          referrerPolicy="no-referrer"
        />
      </AnimatePresence>
    </div>
  );
};

const FunnelModal = ({ 
  isOpen, 
  onClose, 
  initialPlatform, 
  isFreeTrial,
  overrideStep,
  externalTrackingCode
}: { 
  isOpen: boolean, 
  onClose: () => void, 
  initialPlatform?: string, 
  isFreeTrial?: boolean,
  overrideStep?: number,
  externalTrackingCode?: string
}) => {
  const { t, lang } = useTranslation();
  const [step, setStep] = useState(2);
  const [trackingCode, setTrackingCode] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPaymentReady, setIsPaymentReady] = useState(false);
  const scrollContainerRef = React.useRef<HTMLDivElement>(null);
  const [formData, setFormData] = useState({
    platform: initialPlatform || (isFreeTrial ? "Instagram" : "Instagram"),
    goal: "",
    companyName: "",
    email: "",
    url: "",
    budget: "",
    message: "",
    commenterUsername: "",
    selectedReaction: "",
    selectedServiceName: "",
    selectedQuantity: "",
    customComments: Array(10).fill(""),
    isCustom: false,
    isCommentLike: false,
    isTelegramReaction: false,
    isFacebookLike: false,
    selectedFacebookEmoji: ""
  });
  const [showFreeSubmission, setShowFreeSubmission] = useState(false);
  const [showRedLine, setShowRedLine] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobileView(window.innerWidth < 640);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  useEffect(() => {
    if (isOpen) {
      if (overrideStep !== undefined) {
        setStep(overrideStep);
      } else {
        setStep(2);
      }
      if (externalTrackingCode) {
        setTrackingCode(externalTrackingCode);
      } else {
        setTrackingCode("");
      }
      setFormData(prev => ({ ...prev, platform: initialPlatform || "Instagram", goal: "" }));
      setIsPaymentReady(false);
    }
  }, [isOpen, initialPlatform, overrideStep, externalTrackingCode]);

  useEffect(() => {
    if (scrollContainerRef.current) {
      scrollContainerRef.current.scrollTop = 0;
    }
    if (step !== 4) {
      setIsPaymentReady(false);
    }
  }, [step]);

  const goalsByPlatform: Record<string, string[]> = {
    "Instagram": t.modals.funnel.platformGoals?.Instagram || ["Wachstum steigern", "Engagement steigern", "Beiträge pushen"],
    "Facebook": t.modals.funnel.platformGoals?.Facebook || ["Reichweite erhöhen", "Ads verbessern"],
    "Google Maps": t.modals.funnel.platformGoals?.GoogleMaps || ["Bewertungen erhöhen", "Ranking verbessern"],
    "TikTok": t.modals.funnel.platformGoals?.TikTok || ["Viral gehen", "Publikum aufbauen", "Trends nutzen"],
    "YouTube": t.modals.funnel.platformGoals?.YouTube || ["Abonnenten gewinnen", "Watchtime erhöhen", "SEO optimieren"],
    "Telegram": t.modals.funnel.platformGoals?.Telegram || ["Mitglieder erhöhen", "Kanal-Aufrufe steigern", "Beiträge pushen"]
  };

  const isPaypalItem1K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "1K" &&
    formData.selectedItem?.name.toLowerCase().includes("likes") &&
    formData.selectedItem?.price?.includes("1,70");

  const isPaypalItem2K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "2K" &&
    formData.selectedItem?.name.toLowerCase().includes("likes") &&
    formData.selectedItem?.price?.includes("2,70");

  const isPaypalItem5K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "5K" &&
    formData.selectedItem?.name.toLowerCase().includes("likes") &&
    formData.selectedItem?.price?.includes("3,40");

  const isPaypalFollowers1K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("followers") || formData.selectedItem?.name.toLowerCase().includes("follower")) &&
    formData.selectedItem?.price?.includes("6,50");

  const isPaypalFollowers2K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("followers") || formData.selectedItem?.name.toLowerCase().includes("follower")) &&
    formData.selectedItem?.price?.includes("8,90");

  const isPaypalFollowers5K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("followers") || formData.selectedItem?.name.toLowerCase().includes("follower")) &&
    formData.selectedItem?.price?.includes("14,99");

  const isPaypalViewers1K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("views") || formData.selectedItem?.name.toLowerCase().includes("viewers") || formData.selectedItem?.name.includes("مشاهدات")) &&
    formData.selectedItem?.price?.includes("1");

  const isPaypalViewers2K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("views") || formData.selectedItem?.name.toLowerCase().includes("viewers") || formData.selectedItem?.name.includes("مشاهدات")) &&
    formData.selectedItem?.price?.includes("1");

  const isPaypalViewers5K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("views") || formData.selectedItem?.name.toLowerCase().includes("viewers") || formData.selectedItem?.name.includes("مشاهدات")) &&
    formData.selectedItem?.price?.includes("3");

  const isPaypalComments1K = 
    formData.platform === "Instagram" &&
    formData.selectedItem?.amount === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("4,50");

  const isPaypalComments250 = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("2");

  const isPaypalComments2K = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("6,10");

  const isPaypalReposts250 = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("reposts") || formData.selectedItem?.name.includes("إعادة نشر")) &&
    formData.selectedItem?.price?.includes("1,70");

  const isPaypalReposts1K = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("reposts") || formData.selectedItem?.name.includes("إعادة نشر")) &&
    formData.selectedItem?.price?.includes("3,20");

  const isPaypalReposts2K = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("reposts") || formData.selectedItem?.name.includes("إعادة نشر")) &&
    formData.selectedItem?.price?.includes("4");

  const isPaypalReposts5K = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("reposts") || formData.selectedItem?.name.includes("إعادة نشر")) &&
    formData.selectedItem?.price?.includes("9,20");

  const isPaypalTikTokLikes = 
    formData.platform === "TikTok" &&
    formData.selectedItem?.name.toLowerCase().includes("likes") &&
    (formData.selectedItem?.price?.includes("1,90") || formData.selectedItem?.price?.includes("2,80") || formData.selectedItem?.price?.includes("4,30"));

  const isPaypalTikTokFollowers250 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("followers") || formData.selectedItem?.name.toLowerCase().includes("follower") || formData.selectedItem?.name.includes("متابعين")) &&
    formData.selectedItem?.price?.includes("3");

  const isPaypalTikTokFollowers1K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("followers") || formData.selectedItem?.name.toLowerCase().includes("follower") || formData.selectedItem?.name.includes("متابعين")) &&
    formData.selectedItem?.price?.includes("6,40");

  const isPaypalTikTokFollowers2K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("followers") || formData.selectedItem?.name.toLowerCase().includes("follower") || formData.selectedItem?.name.includes("متابعين")) &&
    formData.selectedItem?.price?.includes("8,90");

  const isPaypalTikTokFollowers5K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("followers") || formData.selectedItem?.name.toLowerCase().includes("follower") || formData.selectedItem?.name.includes("متابعين")) &&
    formData.selectedItem?.price?.includes("14,99");

  const isPaypalTikTokViews1K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("views") || formData.selectedItem?.name.toLowerCase().includes("viewers") || formData.selectedItem?.name.includes("مشاهدات")) &&
    formData.selectedItem?.price?.includes("1,50");

  const isPaypalTikTokViews2K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("views") || formData.selectedItem?.name.toLowerCase().includes("viewers") || formData.selectedItem?.name.includes("مشاهدات")) &&
    formData.selectedItem?.price?.includes("2,20");

  const isPaypalTikTokViews5K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("views") || formData.selectedItem?.name.toLowerCase().includes("viewers") || formData.selectedItem?.name.includes("مشاهدات")) &&
    formData.selectedItem?.price?.includes("3,80");

  const isPaypalTikTokComments1K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("4,50");

  const isPaypalTikTokComments2K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("6,10");

  const isPaypalTikTokSaves250 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("saves") || formData.selectedItem?.name.toLowerCase().includes("save") || formData.selectedItem?.name.includes("حفظ")) &&
    formData.selectedItem?.price?.includes("1,70");

  const isPaypalTikTokSaves1K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("saves") || formData.selectedItem?.name.toLowerCase().includes("save") || formData.selectedItem?.name.includes("حفظ")) &&
    formData.selectedItem?.price?.includes("3,20");

  const isPaypalTikTokSaves2K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("saves") || formData.selectedItem?.name.toLowerCase().includes("save") || formData.selectedItem?.name.includes("حفظ")) &&
    formData.selectedItem?.price?.includes("4");

  const isPaypalTikTokSaves5K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("saves") || formData.selectedItem?.name.toLowerCase().includes("save") || formData.selectedItem?.name.includes("حفظ")) &&
    formData.selectedItem?.price?.includes("9,20");

  const isPaypalTikTokShares250 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("1,40");

  const isPaypalTikTokShares1K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("2,50");

  const isPaypalTikTokShares2K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("3,80");

  const isPaypalTikTokShares5K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("8,50");

  const isPaypalFacebookLikes250 = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("likes") || formData.selectedItem?.name.toLowerCase().includes("gefällt") || formData.selectedItem?.name.includes("إعجاب")) &&
    formData.selectedItem?.price?.includes("0,90");

  const isPaypalFacebookLikes1K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("likes") || formData.selectedItem?.name.toLowerCase().includes("gefällt") || formData.selectedItem?.name.includes("إعجاب")) &&
    formData.selectedItem?.price?.includes("2,30");

  const isPaypalFacebookLikes2K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("likes") || formData.selectedItem?.name.toLowerCase().includes("gefällt") || formData.selectedItem?.name.includes("إعجاب")) &&
    formData.selectedItem?.price?.includes("3,50");

  const isPaypalFacebookLikes5K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("likes") || formData.selectedItem?.name.toLowerCase().includes("gefällt") || formData.selectedItem?.name.includes("إعجاب")) &&
    formData.selectedItem?.price?.includes("4,20");

  const isPaypalFacebookComments250 = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("2");

  const isPaypalFacebookComments1K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("4,50");

  const isPaypalFacebookComments2K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("6,10");

  const isPaypalFacebookComments5K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("8,80");

  const isPaypalFacebookShares250 = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("1,70");

  const isPaypalFacebookShares1K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "1K" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("3,20");

  const isPaypalFacebookShares2K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "2K" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("4");

  const isPaypalFacebookShares5K = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "5K" &&
    (formData.selectedItem?.name.toLowerCase().includes("shares") || formData.selectedItem?.name.toLowerCase().includes("share") || formData.selectedItem?.name.includes("مشاركات")) &&
    formData.selectedItem?.price?.includes("9,20");

  const isPaypalTikTokCommentLikes250 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount).toLowerCase() === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("1,60");

  const isPaypalTikTokCommentLikes500 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount).toLowerCase() === "500" &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("2,20");

  const isPaypalTikTokCommentLikes1K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount).toLowerCase() === "1k" &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("3,50");

  const isPaypalTikTokCommentLikes3K = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount).toLowerCase() === "3k" &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("4,80");

  const isPaypalInstagramCommentLikes250 = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount).toLowerCase() === "250" &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("1,60");

  const isPaypalInstagramCommentLikes500 = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount).toLowerCase() === "500" &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("2,20");

  const isPaypalInstagramCommentLikes1K = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount).toLowerCase() === "1k" &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("3,50");

  const isPaypalInstagramCommentLikes2K = 
    formData.platform === "Instagram" &&
    (String(formData.selectedItem?.amount).toLowerCase() === "2k" || String(formData.selectedItem?.amount).toLowerCase() === "3k") &&
    (formData.selectedItem?.name.toLowerCase().includes("comment likes") || formData.selectedItem?.name.toLowerCase().includes("kommentar")) &&
    formData.selectedItem?.price?.includes("4,80");

  const isPaypalTikTokCustomComments10 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "10" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("1,90");

  const isPaypalTikTokCustomComments20 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "20" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("3,50");

  const isPaypalTikTokCustomComments40 = 
    formData.platform === "TikTok" &&
    String(formData.selectedItem?.amount) === "40" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("4,90");

  const isPaypalInstagramCustomComments10 = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "10" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("1,90");

  const isPaypalInstagramCustomComments20 = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "20" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("3,50");

  const isPaypalInstagramCustomComments40 = 
    formData.platform === "Instagram" &&
    String(formData.selectedItem?.amount) === "40" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("4,90");

  const isPaypalFacebookCustomComments10 = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "10" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("1,90");

  const isPaypalFacebookCustomComments20 = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "20" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("3,50");

  const isPaypalFacebookCustomComments40 = 
    formData.platform === "Facebook" &&
    String(formData.selectedItem?.amount) === "40" &&
    (formData.selectedItem?.name.toLowerCase().includes("selbst ausgewählte") || formData.selectedItem?.name.toLowerCase().includes("custom") || formData.selectedItem?.name.includes("مخصص")) &&
    (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) &&
    formData.selectedItem?.price?.includes("4,90");

  const isPaypalItem = isPaypalItem1K || isPaypalItem2K || isPaypalItem5K || isPaypalFollowers1K || isPaypalFollowers2K || isPaypalFollowers5K || isPaypalViewers1K || isPaypalViewers2K || isPaypalViewers5K || isPaypalComments1K || isPaypalComments250 || isPaypalComments2K || isPaypalReposts250 || isPaypalReposts1K || isPaypalReposts2K || isPaypalReposts5K || isPaypalTikTokLikes || isPaypalTikTokFollowers250 || isPaypalTikTokFollowers1K || isPaypalTikTokFollowers2K || isPaypalTikTokFollowers5K || isPaypalTikTokViews1K || isPaypalTikTokViews2K || isPaypalTikTokViews5K || isPaypalTikTokComments1K || isPaypalTikTokComments2K || isPaypalTikTokSaves250 || isPaypalTikTokSaves1K || isPaypalTikTokSaves2K || isPaypalTikTokSaves5K || isPaypalTikTokShares250 || isPaypalTikTokShares1K || isPaypalTikTokShares2K || isPaypalTikTokShares5K || isPaypalFacebookLikes250 || isPaypalFacebookLikes1K || isPaypalFacebookLikes2K || isPaypalFacebookLikes5K || isPaypalFacebookComments250 || isPaypalFacebookComments1K || isPaypalFacebookComments2K || isPaypalFacebookComments5K || isPaypalFacebookShares250 || isPaypalFacebookShares1K || isPaypalFacebookShares2K || isPaypalFacebookShares5K || isPaypalTikTokCommentLikes250 || isPaypalTikTokCommentLikes500 || isPaypalTikTokCommentLikes1K || isPaypalTikTokCommentLikes3K || isPaypalInstagramCommentLikes250 || isPaypalInstagramCommentLikes500 || isPaypalInstagramCommentLikes1K || isPaypalInstagramCommentLikes2K || isPaypalTikTokCustomComments10 || isPaypalTikTokCustomComments20 || isPaypalTikTokCustomComments40 || isPaypalInstagramCustomComments10 || isPaypalInstagramCustomComments20 || isPaypalInstagramCustomComments40 || isPaypalFacebookCustomComments10 || isPaypalFacebookCustomComments20 || isPaypalFacebookCustomComments40;
  
  const registerOrder = async () => {
    // Generate random 6-digit code
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let code = '';
    for (let i = 0; i < 6; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setTrackingCode(code);

    // Save to Firestore
    const orderId = `order_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
    
    // Facebook Emoji URL to Text Mapping
    let mappedEmoji = formData.selectedFacebookEmoji;
    if (formData.selectedFacebookEmoji) {
      const emojiMapping: Record<string, string> = {
        "https://i.postimg.cc/76gVFzGc/Unthhitled-removebg-preview.png": "loving",
        "https://i.postimg.cc/J7FV7VGF/22Untitled-removebg-preview.png": "being mad",
        "https://i.postimg.cc/RCgCcDqm/Untitlsed-removebg-preview.png": "smiling",
        "https://i.postimg.cc/V6CfmfmX/Screenshot-2026-05-0dd6-001107-removebg-preview(3).png": "thumbs up",
        "https://i.postimg.cc/QxWthqxB/Gemini-Generateddada-Image-1j0vym1j0vym1j0v-removebg-preview-1-removebg-preview(1).png": "heart"
      };
      mappedEmoji = emojiMapping[formData.selectedFacebookEmoji] || "unknown emoji";
    }

    const serviceNameLower = (formData.selectedServiceName || "").toLowerCase() || (formData.goal || "").toLowerCase();
    let orderType = 'other';
    if (serviceNameLower.includes('like')) {
      orderType = 'likes';
    } else if (serviceNameLower.includes('follower')) {
      orderType = 'followers';
    } else if (serviceNameLower.includes('comment')) {
      orderType = 'comments';
    } else if (serviceNameLower.includes('view') || serviceNameLower.includes('aufrufe')) {
      orderType = 'views';
    } else if (serviceNameLower.includes('member') || serviceNameLower.includes('mitglied')) {
      orderType = 'members';
    } else if (serviceNameLower.includes('reaction')) {
      orderType = 'reactions';
    }

    const now = new Date();
    const dateTitle = now.toLocaleDateString('de-DE') + ' ' + now.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });

    const orderData = Object.fromEntries(
      Object.entries({
        title: dateTitle,
        trackingCode: code,
        status: 'Processing',
        platform: formData.platform || "Unknown",
        goal: formData.goal || "Unknown Service",
        quantity: formData.selectedQuantity || "Default Quantity",
        price: formData.selectedItem?.price || "Free Trial",
        url: formData.url,
        commenterUsername: formData.commenterUsername,
        customComments: formData.isCustom ? formData.customComments.map((c: string) => c.trim()).filter((c: string) => c !== "").join('\n') : null,
        selectedReaction: formData.selectedReaction || null,
        selectedFacebookEmoji: mappedEmoji || null,
        createdAt: serverTimestamp(),
        createdAtMillis: Date.now(),
        createdAtStr: now.toISOString(),
        statusNormalized: 'processing',
        platformNormalized: (formData.platform || "unknown").toLowerCase(),
        orderType: orderType,
        priorityBucket: 'urgent',
        adminLabel: `[Processing] ${formData.platform || 'Unknown'} - ${formData.goal || 'Goal'} - Qty: ${formData.selectedQuantity || ''} - Code: ${code}`,
        searchTokens: [
          code.toLowerCase(),
          (formData.platform || "").toLowerCase(),
          'processing',
          orderType,
          (formData.goal || "").toLowerCase()
        ].filter(Boolean)
      }).filter(([_, v]) => v !== null && v !== undefined && v !== "")
    );

    addBreadcrumb({
      category: 'checkout',
      message: 'Firestore write started',
      level: 'info',
      data: { orderId, platform: formData.platform, goal: formData.goal, price: formData.selectedItem?.price }
    });
    captureMessage('Firestore write started', {
      extra: { orderId, platform: formData.platform, goal: formData.goal, url: formData.url }
    });

    try {
      await setDoc(doc(db, "Orders", orderId), orderData);
      
      addBreadcrumb({
        category: 'checkout',
        message: 'Firestore write succeeded',
        level: 'info',
        data: { orderId, trackingCode: code }
      });
      captureMessage('Firestore write succeeded', {
        extra: { orderId, trackingCode: code }
      });
      return code;
    } catch (error) {
      addBreadcrumb({
        category: 'checkout',
        message: 'Firestore write failed',
        level: 'error',
        data: { orderId, error: String(error) }
      });
      captureMessage('Firestore write failed', {
        level: 'error',
        extra: { orderId, error: String(error) }
      });
      captureException(error, {
        extra: { orderId, orderData }
      });
      throw error;
    }
  };

  const handleNext = async () => {
    if (step === 3) {
      addBreadcrumb({
        category: 'checkout',
        message: 'Order submitted',
        level: 'info',
        data: { step, platform: formData.platform, goal: formData.goal, url: formData.url }
      });
      captureMessage('Order submitted', {
        extra: { platform: formData.platform, goal: formData.goal, url: formData.url }
      });

      const isValid = Boolean(formData.url && (!formData.isCommentLike || formData.commenterUsername) && (!formData.isTelegramReaction || formData.selectedReaction));
      if (isValid) {
        addBreadcrumb({
          category: 'checkout',
          message: 'Validation passed',
          level: 'info',
          data: { url: formData.url }
        });
        captureMessage('Validation passed', {
          extra: { url: formData.url, platform: formData.platform }
        });
      }
      setStep(4);
    } else if (step === 4) {
      if (isPaypalItem) {
        return;
      }

      setIsSubmitting(true);
      try {
        await registerOrder();
        setStep(5);
      } catch (error) {
        alert("Failed to save order: " + (error instanceof Error ? error.message : String(error)));
        handleFirestoreError(error, OperationType.WRITE, `Orders`);
      } finally {
        setIsSubmitting(false);
      }
    } else {
      setStep(step + 1);
    }
  };
  const handleBack = () => setStep(step - 1);

  const [activeColumn, setActiveColumn] = useState<number | null>(null);
  const [activeRow, setActiveRow] = useState<number | null>(null);
  const [activeTable, setActiveTable] = useState<string | null>(null);

  useEffect(() => {
    if (step === 4 && isFreeTrial && isPaypalViewers1K) {
      const lineTimer = setTimeout(() => {
        setShowRedLine(true);
      }, 300);
      const buttonTimer = setTimeout(() => {
        setShowFreeSubmission(true);
      }, 2000);
      return () => {
        clearTimeout(lineTimer);
        clearTimeout(buttonTimer);
      };
    } else {
      setShowRedLine(false);
      setShowFreeSubmission(false);
    }
  }, [step, isFreeTrial, isPaypalViewers1K]);

  useEffect(() => {
    if (step === 4 && isPaypalItem) {
      // Small delay to ensure the container is in the DOM
      const timer = setTimeout(() => {
        const handlePaypalCapture = async (data?: any, actions?: any) => {
          setIsSubmitting(true);
          const orderId = data?.orderID || data?.id;

          addBreadcrumb({
            category: 'checkout',
            message: 'Order submitted',
            level: 'info',
            data: { paypalOrderId: orderId, platform: formData.platform, goal: formData.goal }
          });
          captureMessage('Order submitted', {
            extra: { paypalOrderId: orderId, formData }
          });

          addBreadcrumb({
            category: 'checkout',
            message: 'Validation passed',
            level: 'info',
            data: { paypalOrderId: orderId }
          });
          captureMessage('Validation passed', {
            extra: { paypalOrderId: orderId }
          });

          addBreadcrumb({
            category: 'checkout',
            message: 'Firestore write started',
            level: 'info',
            data: { paypalOrderId: orderId, endpoint: 'capturePayment' }
          });
          captureMessage('Firestore write started', {
            extra: { paypalOrderId: orderId, endpoint: 'capturePayment' }
          });

          try {
            const response = await fetch(
              'https://capturepayment-zote4sur7a-uc.a.run.app',
              {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  orderID: orderId,
                  formData: formData
                })
              }
            );

            const result = await response.json().catch(() => ({}));

            if (!response.ok || result?.status !== 'COMPLETED') {
              const err = new Error(result?.error || result?.details || `HTTP ${response.status}`);
              addBreadcrumb({
                category: 'checkout',
                message: 'Firestore write failed',
                level: 'error',
                data: { paypalOrderId: orderId, result, status: response.status }
              });
              captureMessage('Firestore write failed', {
                level: 'error',
                extra: { paypalOrderId: orderId, result, status: response.status }
              });
              captureException(err, {
                extra: { paypalOrderId: orderId, result }
              });
              throw err;
            }

            const code = result.trackingCode || 'CAPTURED';
            addBreadcrumb({
              category: 'checkout',
              message: 'Firestore write succeeded',
              level: 'info',
              data: { paypalOrderId: orderId, trackingCode: code }
            });
            captureMessage('Firestore write succeeded', {
              extra: { paypalOrderId: orderId, trackingCode: code }
            });

            setTrackingCode(code);
            setStep(5);
          } catch (error) {
            console.error('PayPal capture error:', error);
            alert("Payment failed or order could not be saved: " + (error instanceof Error ? error.message : String(error)));
          } finally {
            setIsSubmitting(false);
          }
        };

        const renderPayPalButton = (containerId: string, hostedButtonId: string) => {
          const container = document.getElementById(containerId);
          if (container && container.innerHTML === "" && (window as any).paypal) {
            (window as any).paypal.HostedButtons({
              hostedButtonId,
              onApprove: handlePaypalCapture,
              onOrderApproved: handlePaypalCapture
            }).render(`#${containerId}`).then(() => {
              setIsPaymentReady(true);
            });
          } else if (container && container.innerHTML !== "") {
            setIsPaymentReady(true);
          }
        };

        if (isPaypalItem1K) renderPayPalButton("paypal-container-X8KDUQT9THV7Q", "X8KDUQT9THV7Q");
        else if (isPaypalItem2K) renderPayPalButton("paypal-container-2G3DDZLGM6DWA", "2G3DDZLGM6DWA");
        else if (isPaypalItem5K) renderPayPalButton("paypal-container-WTSWWJW4TUF4U", "WTSWWJW4TUF4U");
        else if (isPaypalFollowers1K) renderPayPalButton("paypal-container-8MFB5LNR6KED6", "8MFB5LNR6KED6");
        else if (isPaypalFollowers2K) renderPayPalButton("paypal-container-E5VCGSTP4EWQJ", "E5VCGSTP4EWQJ");
        else if (isPaypalFollowers5K) renderPayPalButton("paypal-container-REAZUDEBCRH4C", "REAZUDEBCRH4C");
        else if (isPaypalViewers1K) renderPayPalButton("paypal-container-LKPWQ2A95ZDJC", "LKPWQ2A95ZDJC");
        else if (isPaypalViewers2K) renderPayPalButton("paypal-container-5G93AU2U9U376", "5G93AU2U9U376");
        else if (isPaypalViewers5K) renderPayPalButton("paypal-container-538LSAPUH4D72", "538LSAPUH4D72");
        else if (isPaypalComments250) renderPayPalButton("paypal-container-MSJT5YYVSS2WE", "MSJT5YYVSS2WE");
        else if (isPaypalComments1K) renderPayPalButton("paypal-container-3DD4322Y8QNW6", "3DD4322Y8QNW6");
        else if (isPaypalComments2K) renderPayPalButton("paypal-container-V6ND3T6J4SYK8", "V6ND3T6J4SYK8");
        else if (isPaypalReposts250) renderPayPalButton("paypal-container-K56UCNZ7XVZ7C", "K56UCNZ7XVZ7C");
        else if (isPaypalReposts1K) renderPayPalButton("paypal-container-AVF5RJZDBD9HG", "AVF5RJZDBD9HG");
        else if (isPaypalReposts2K) renderPayPalButton("paypal-container-AVF5RJZDBD9HG", "AVF5RJZDBD9HG");
        else if (isPaypalReposts5K) renderPayPalButton("paypal-container-C97JWTYX5PMFY", "C97JWTYX5PMFY");
        else if (isPaypalTikTokLikes) renderPayPalButton("paypal-container-3J39D4FFH9A3L", "3J39D4FFH9A3L");
        else if (isPaypalTikTokFollowers250) renderPayPalButton("paypal-container-7MYGBSLD87W86", "7MYGBSLD87W86");
        else if (isPaypalTikTokFollowers1K) renderPayPalButton("paypal-container-R87HPPMKZX33S", "R87HPPMKZX33S");
        else if (isPaypalTikTokFollowers2K) renderPayPalButton("paypal-container-ZCB56DJJ8S3SJ", "ZCB56DJJ8S3SJ");
        else if (isPaypalTikTokFollowers5K) renderPayPalButton("paypal-container-74JLMHECY9KQG", "74JLMHECY9KQG");
        else if (isPaypalTikTokViews1K) renderPayPalButton("paypal-container-4NY4UR8UXZ4FC", "4NY4UR8UXZ4FC");
        else if (isPaypalTikTokViews2K) renderPayPalButton("paypal-container-4PWERUW8UETQL", "4PWERUW8UETQL");
        else if (isPaypalTikTokViews5K) renderPayPalButton("paypal-container-E2YMHA6WPFHFG", "E2YMHA6WPFHFG");
        else if (isPaypalTikTokComments1K) renderPayPalButton("paypal-container-S3ST3SGQUZEPA", "S3ST3SGQUZEPA");
        else if (isPaypalTikTokComments2K) renderPayPalButton("paypal-container-M2LT7ED7FLBQN", "M2LT7ED7FLBQN");
        else if (isPaypalTikTokSaves250) renderPayPalButton("paypal-container-KKE3M6V5K43NN", "KKE3M6V5K43NN");
        else if (isPaypalTikTokSaves1K) renderPayPalButton("paypal-container-GCX3V82UPU42A", "GCX3V82UPU42A");
        else if (isPaypalTikTokSaves2K) renderPayPalButton("paypal-container-PPRCSS889V3ZQ", "PPRCSS889V3ZQ");
        else if (isPaypalTikTokSaves5K) renderPayPalButton("paypal-container-SKT72752QVZWU", "SKT72752QVZWU");
        else if (isPaypalTikTokShares250) renderPayPalButton("paypal-container-FGN6XE8B58NPE", "FGN6XE8B58NPE");
        else if (isPaypalTikTokShares1K) renderPayPalButton("paypal-container-FSBRBFYR3VZSQ", "FSBRBFYR3VZSQ");
        else if (isPaypalTikTokShares2K) renderPayPalButton("paypal-container-6TQ3KNCHLH2PW", "6TQ3KNCHLH2PW");
        else if (isPaypalTikTokShares5K) renderPayPalButton("paypal-container-7VTBDYGNBXPVW", "7VTBDYGNBXPVW");
        else if (isPaypalFacebookLikes250) renderPayPalButton("paypal-container-53L37KANNPC4W", "53L37KANNPC4W");
        else if (isPaypalFacebookLikes1K) renderPayPalButton("paypal-container-R4D3C9G7QPVE8", "R4D3C9G7QPVE8");
        else if (isPaypalFacebookLikes2K) renderPayPalButton("paypal-container-CGR3YL6WLWE9W", "CGR3YL6WLWE9W");
        else if (isPaypalFacebookLikes5K) renderPayPalButton("paypal-container-7U476TWUZUM3C", "7U476TWUZUM3C");
        else if (isPaypalFacebookComments250) renderPayPalButton("paypal-container-9B4FQMPVGW69L", "9B4FQMPVGW69L");
        else if (isPaypalFacebookComments1K) renderPayPalButton("paypal-container-CP2PUPXUQW582", "CP2PUPXUQW582");
        else if (isPaypalFacebookComments2K) renderPayPalButton("paypal-container-DWYCXQMGWD8X4", "DWYCXQMGWD8X4");
        else if (isPaypalFacebookComments5K) renderPayPalButton("paypal-container-ATH9MZJQPY9XE", "ATH9MZJQPY9XE");
        else if (isPaypalFacebookShares250) renderPayPalButton("paypal-container-5UUKUWXV3F4FW", "5UUKUWXV3F4FW");
        else if (isPaypalFacebookShares1K) renderPayPalButton("paypal-container-X5Q83ENVAS3WE", "X5Q83ENVAS3WE");
        else if (isPaypalFacebookShares2K) renderPayPalButton("paypal-container-6TJ2JA6AAZDPU", "6TJ2JA6AAZDPU");
        else if (isPaypalFacebookShares5K) renderPayPalButton("paypal-container-EAYALQGRHPUNC", "EAYALQGRHPUNC");
        else if (isPaypalTikTokCommentLikes250) renderPayPalButton("paypal-container-Y9CQLUG7LXASA", "Y9CQLUG7LXASA");
        else if (isPaypalTikTokCommentLikes500) renderPayPalButton("paypal-container-QTYDQFR9KUX8C", "QTYDQFR9KUX8C");
        else if (isPaypalTikTokCommentLikes1K) renderPayPalButton("paypal-container-TYXS2WMDJC24U", "TYXS2WMDJC24U");
        else if (isPaypalTikTokCommentLikes3K) renderPayPalButton("paypal-container-9DZHHJU57HKF2", "9DZHHJU57HKF2");
        else if (isPaypalInstagramCommentLikes250) renderPayPalButton("paypal-container-4CUF8FJ5Y2TTN", "4CUF8FJ5Y2TTN");
        else if (isPaypalInstagramCommentLikes500) renderPayPalButton("paypal-container-FG8AV2H5LJ2XS", "FG8AV2H5LJ2XS");
        else if (isPaypalInstagramCommentLikes1K) renderPayPalButton("paypal-container-ZGWKWC99HYG5C", "ZGWKWC99HYG5C");
        else if (isPaypalInstagramCommentLikes2K) renderPayPalButton("paypal-container-Y4FPTHLVCPMT8", "Y4FPTHLVCPMT8");
        else if (isPaypalTikTokCustomComments10) renderPayPalButton("paypal-container-6DFBU9CTKD8VQ", "6DFBU9CTKD8VQ");
        else if (isPaypalTikTokCustomComments20) renderPayPalButton("paypal-container-ABGAK8EG6A8GC", "ABGAK8EG6A8GC");
        else if (isPaypalTikTokCustomComments40) renderPayPalButton("paypal-container-VVKLU6QZZBNMG", "VVKLU6QZZBNMG");
        else if (isPaypalInstagramCustomComments10) renderPayPalButton("paypal-container-8RZXCJ8Q5MBB6", "8RZXCJ8Q5MBB6");
        else if (isPaypalInstagramCustomComments20) renderPayPalButton("paypal-container-KYX5M48QAQUT6", "KYX5M48QAQUT6");
        else if (isPaypalInstagramCustomComments40) renderPayPalButton("paypal-container-8Q7MYU8WJT3M4", "8Q7MYU8WJT3M4");
        else if (isPaypalFacebookCustomComments10) renderPayPalButton("paypal-container-3YCH6G8HNK2ZN", "3YCH6G8HNK2ZN");
        else if (isPaypalFacebookCustomComments20) renderPayPalButton("paypal-container-29FEQCTQ55DH6", "29FEQCTQ55DH6");
        else if (isPaypalFacebookCustomComments40) renderPayPalButton("paypal-container-PGH42KE9KHHH6", "PGH42KE9KHHH6");
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [step, isPaypalItem, isPaypalItem1K, isPaypalItem2K, isPaypalItem5K, isPaypalFollowers1K, isPaypalFollowers2K, isPaypalFollowers5K, isPaypalViewers1K, isPaypalViewers2K, isPaypalViewers5K, isPaypalComments1K, isPaypalComments250, isPaypalComments2K, isPaypalReposts250 || isPaypalReposts1K || isPaypalReposts2K || isPaypalReposts5K, isPaypalTikTokLikes, isPaypalTikTokFollowers250, isPaypalTikTokFollowers1K, isPaypalTikTokFollowers2K, isPaypalTikTokFollowers5K, isPaypalTikTokViews1K, isPaypalTikTokViews2K, isPaypalTikTokViews5K, isPaypalTikTokComments1K, isPaypalTikTokComments2K, isPaypalTikTokSaves250, isPaypalTikTokSaves1K, isPaypalTikTokSaves2K, isPaypalTikTokSaves5K, isPaypalTikTokShares250, isPaypalTikTokShares1K, isPaypalTikTokShares2K, isPaypalTikTokShares5K, isPaypalFacebookLikes250, isPaypalFacebookLikes1K, isPaypalFacebookLikes2K, isPaypalFacebookLikes5K, isPaypalFacebookComments250, isPaypalFacebookComments1K, isPaypalFacebookComments2K, isPaypalFacebookComments5K, isPaypalFacebookShares250, isPaypalFacebookShares1K, isPaypalFacebookShares2K, isPaypalFacebookShares5K, isPaypalTikTokCommentLikes250, isPaypalTikTokCommentLikes500, isPaypalTikTokCommentLikes1K, isPaypalTikTokCommentLikes3K, isPaypalInstagramCommentLikes250, isPaypalInstagramCommentLikes500, isPaypalInstagramCommentLikes1K, isPaypalInstagramCommentLikes2K, isPaypalTikTokCustomComments10, isPaypalTikTokCustomComments20, isPaypalTikTokCustomComments40, isPaypalInstagramCustomComments10, isPaypalInstagramCustomComments20, isPaypalInstagramCustomComments40, isPaypalFacebookCustomComments10, isPaypalFacebookCustomComments20, isPaypalFacebookCustomComments40]);

const isAccountRelated = (formData.platform === "Instagram" && (formData.selectedServiceName?.toLowerCase().includes("follower") || formData.selectedItem?.name?.toLowerCase().includes("follower"))) ||
    (formData.platform === "Telegram" && (formData.selectedServiceName?.toLowerCase().includes("member") || formData.selectedItem?.name?.toLowerCase().includes("أعضاء")));

  const urlLabel = isAccountRelated
    ? (lang === 'de' ? 'Konto Link' : lang === 'ar' ? 'رابط الحساب' : 'Account Link')
    : t.modals.funnel.labels.url;

  if (!isOpen && !step) return null; // Keep a small guard but rely on AnimatePresence

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-[100] flex items-center justify-center p-3 md:p-6"
      >
        <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" onClick={onClose} />
        
        <motion.div
          initial={{ scale: 0.9, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.9, opacity: 0, y: 20 }}
          className="relative w-full max-w-5xl liquid-glass rounded-[2.5rem] overflow-hidden shadow-2xl border border-white/20 flex flex-col max-h-[90vh]"
        >
          {/* Header Area - Persistent */}
          <div className="relative flex-none z-30 px-6 sm:px-10 py-4 sm:py-6 bg-transparent">
            {/* Progress Bar (Stay stable at top) */}
            <div className={`flex gap-2 ${formData.platform === "Google Maps" ? "mt-1" : ""}`}>
              {[2, 3, 4].map((s) => (
                <div 
                  key={s} 
                  className={`h-1.5 flex-grow rounded-full transition-all duration-500 ${s <= step ? "bg-blue-500" : "bg-white/10"}`}
                />
              ))}
            </div>

            <button 
              onClick={onClose}
              className={`absolute right-6 sm:right-10 text-white/50 hover:text-white transition-colors z-20 ${formData.platform === "Google Maps" ? "top-8 sm:top-12" : "top-6 sm:top-10"}`}
              style={
                !isMobileView 
                  ? { paddingLeft: '0px', marginLeft: '6px', width: '62.7833px', height: '28px', marginTop: '28px' }
                  : { marginLeft: '13px', paddingTop: '-2px', marginTop: '26px' }
              }
            >
              <X size={24} />
            </button>
          </div>

          <div 
            ref={scrollContainerRef}
            className={`flex-1 overflow-y-auto custom-scrollbar px-2 sm:px-10 pb-8 sm:pb-12 ${formData.platform === 'Google Maps' ? 'pt-0' : 'pt-2'}`}
          >
            {formData.isCustom && step === 3 && (
              <motion.p 
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center text-xs sm:text-sm font-bold text-blue-400/80 uppercase tracking-[0.2em] mb-8"
              >
                {(t.modals.funnel.buttons as any).customStep?.statement}
              </motion.p>
            )}

            {step === 2 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }}>
                <h2 className={`text-xl sm:text-2xl font-black mb-4 sm:mb-8 ${
                  formData.platform === "Google Maps" 
                    ? "mt-16 ml-6 sm:mt-24 sm:ml-12" 
                    : "mt-8 ml-2 sm:mt-10 sm:ml-4"
                }`}>
                  {formData.platform === "Google Maps" ? t.modals.funnel.buttons.beCreative : t.modals.funnel.step2}
                </h2>
                
                {(() => {
                  const isPlatformTable = ["Instagram", "Facebook", "TikTok", "Telegram", "Google Maps"].includes(formData.platform);
                  
                  if (isPlatformTable) {
                    const renderTable = (items: any[], headers: any[], tableId: string, rowOffset = 0, showPrefix = false, customButtonText?: string, headerSuffix?: string, customServiceHeader?: string) => (
                      <table className="w-full text-left border-collapse table-fixed">
                        <thead>
                          <tr className="border-b border-white/10">
                            <th className={`p-2 sm:p-6 font-bold uppercase tracking-widest w-[30%] sm:w-auto ${
                              formData.platform === "Google Maps"
                                ? `iridescent-text ${lang === 'ar' ? 'text-[11px] sm:text-sm' : 'text-[8px] sm:text-[10px]'} translate-x-1 sm:translate-x-0`
                                : `text-white/50 ${lang === 'ar' ? 'text-[11px] sm:text-sm' : 'text-[8px] sm:text-[10px]'}`
                            }`}>
                              {customServiceHeader || (lang === 'de' ? 'SERVICE' : lang === 'ar' ? 'الخدمة' : 'SERVICE')}
                            </th>
                            {headers.map((q, idx) => {
                              const colIdx = idx + 1;
                              const isActive = activeTable === tableId && activeColumn === colIdx;
                              const isDimmed = activeTable === tableId && activeColumn !== null && activeColumn !== colIdx;
                              return (
                                <th 
                                  key={q} 
                                  className={`p-2 sm:p-6 text-white font-black uppercase tracking-widest text-xs sm:text-xl text-center transition-all duration-300 ease-in-out w-[17.5%] sm:w-auto ${
                                    isActive ? "opacity-100 scale-110 brightness-200 drop-shadow-[0_0_10px_rgba(255,255,255,0.7)]" : 
                                    isDimmed ? "opacity-20" : "opacity-100"
                                  }`}
                                >
                                  <div className="flex flex-col sm:flex-row items-center justify-center gap-0.5 sm:gap-1.5">
                                    {showPrefix && t.modals.funnel.buttons.upTo && (
                                      <span className={`sm:hidden font-bold uppercase tracking-tighter leading-none mb-0.5 ${
                                        formData.platform === 'Google Maps'
                                          ? "iridescent-text text-[7px] translate-x-1"
                                          : "text-[7px] text-white/60"
                                      }`}>
                                        {t.modals.funnel.buttons.upTo}
                                      </span>
                                    )}
                                    <div className="flex items-center justify-center gap-1">
                                      {formData.platform === "Google Maps" && showPrefix && t.modals.funnel.buttons.upTo && (
                                        <span className="hidden sm:inline text-[10px] iridescent-text font-bold normal-case tracking-normal">
                                          {t.modals.funnel.buttons.upTo}
                                        </span>
                                      )}
                                      <span className="leading-none text-xs sm:text-xl font-black">{q}</span>
                                      {headerSuffix && (
                                        <span className={`hidden sm:inline text-[10px] font-bold normal-case tracking-normal ml-1 ${
                                          formData.platform === 'Google Maps' ? "iridescent-text" : "text-white/40"
                                        }`}>
                                          {headerSuffix}
                                        </span>
                                      )}
                                    </div>
                                    {headerSuffix && (
                                      <span className={`sm:hidden font-bold uppercase tracking-tighter leading-none mt-0.5 ${
                                        formData.platform === 'Google Maps'
                                          ? "iridescent-text text-[7px] translate-x-1"
                                          : "text-[7px] text-white/60"
                                      }`}>
                                        {headerSuffix}
                                      </span>
                                    )}
                                  </div>
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {items.map((item, i) => (
                            <tr 
                              key={i} 
                              className={`border-b border-white/5 transition-all duration-300 ease-in-out ${
                                activeTable === tableId && activeRow === (i + rowOffset) ? "bg-white/10" : "hover:bg-white/5"
                              }`}
                            >
                              <td className="py-2 sm:py-4 pr-1 sm:pr-6 pl-2 sm:pl-10 font-bold text-[10px] sm:text-lg">
                                <div className="flex items-center gap-1 sm:gap-2">
                                  <div className="flex flex-col items-start text-left leading-tight no-rtl">
                                    {['TikTok', 'Instagram', 'Facebook', 'Telegram', 'Google Maps'].includes(formData.platform) ? (
                                      <>
                                        {formData.platform !== 'Google Maps' && <span>{formData.platform}</span>}
                                        <span 
                                          className="flex items-center gap-1"
                                          style={
                                            isMobileView && formData.platform === 'Instagram' && (item.name.includes('Kommentare') || item.name.includes('Comments') || item.name.includes('تعليقات'))
                                              ? { paddingTop: '-2px', paddingLeft: '-2px', marginLeft: '-3px' }
                                              : undefined
                                          }
                                        >
                                          {formData.platform === 'Google Maps' ? item.name : item.name.replace(`${formData.platform} `, '')}
                                          {formData.platform === 'TikTok' && (item.name.toLowerCase().includes('likes') || item.name.includes('Gefällt mir') || (typeof item.name === 'string' && item.name.includes('إعجاب'))) && (
                                            <img 
                                              src="https://i.postimg.cc/RVfSCzqH/insta-heart-removebg-preview.png" 
                                              alt="emoji" 
                                              className="w-6 h-6 sm:w-8 sm:h-8 object-contain ml-6 sm:ml-8 -translate-y-1 sm:-translate-y-2 inline-block"
                                              style={
                                                isMobileView 
                                                  ? { paddingLeft: '-6px', paddingTop: '0px', paddingRight: '-1px', marginBottom: '-1px', marginTop: '-9px', marginLeft: '24px' } 
                                                  : undefined
                                              }
                                              referrerPolicy="no-referrer"
                                            />
                                          )}
                                          {formData.platform === 'TikTok' && (item.name.toLowerCase().includes('views') || item.name.includes('Aufrufe') || (typeof item.name === 'string' && item.name.includes('مشاهدات'))) && (
                                            <div className="relative inline-block w-0 h-0 ml-4 sm:ml-6">
                                              <img 
                                                src="https://i.postimg.cc/8zwyZLQ8/Un2titled-removebg-preview(1).png" 
                                                alt="emoji" 
                                                className="w-10 h-10 sm:w-14 sm:h-14 object-contain absolute left-0 bottom-[-8px] sm:bottom-[-14px] max-w-none"
                                                style={
                                                  isMobileView 
                                                    ? { marginLeft: '-6px', marginTop: '-2px', marginBottom: '-5px' } 
                                                    : undefined
                                                }
                                                referrerPolicy="no-referrer"
                                              />
                                            </div>
                                          )}
                                          {formData.platform === 'TikTok' && (item.name.toLowerCase().includes('saves') || item.name.includes('Speicherungen') || (typeof item.name === 'string' && item.name.includes('حفظ'))) && (
                                            <div className="relative inline-block w-0 h-0 ml-5 sm:ml-7">
                                              <img 
                                                src="https://i.postimg.cc/Dwb8sdCQ/Untitled-tt-removebg-preview.png" 
                                                alt="emoji" 
                                                className="w-8 h-8 sm:w-10 sm:h-10 object-contain absolute left-0 bottom-[-2px] sm:bottom-[-4px] max-w-none"
                                                style={
                                                  isMobileView 
                                                    ? { marginLeft: '-5px', marginTop: '2px', marginBottom: '-10px' } 
                                                    : undefined
                                                }
                                                referrerPolicy="no-referrer"
                                              />
                                            </div>
                                          )}
                                          {formData.platform === 'TikTok' && (item.name.toLowerCase().includes('shares') || item.name.includes('Teilen') || (typeof item.name === 'string' && item.name.includes('مشاركة'))) && (
                                            <img 
                                              src="https://i.postimg.cc/B6v88WkN/Untitledtts-removebg-preview.png" 
                                              alt="emoji" 
                                              className="w-6 h-6 sm:w-8 sm:h-8 object-contain ml-2 sm:ml-3 -translate-y-1 sm:-translate-y-2 inline-block"
                                              referrerPolicy="no-referrer"
                                            />
                                          )}
                                          {formData.platform === 'TikTok' && (item.name.toLowerCase().includes('comments') || item.name.includes('Kommentare') || (typeof item.name === 'string' && item.name.includes('تعليقات'))) && (
                                            <img 
                                              src="https://i.postimg.cc/d1v1bh8p/untiteled-comments-removebg-preview(1).png" 
                                              alt="emoji" 
                                              className="w-6 h-6 sm:w-8 sm:h-8 object-contain ml-2 sm:ml-3 -translate-y-1 sm:-translate-y-2 inline-block"
                                              style={{ marginLeft: isMobileView ? '9px' : '16px', width: '38px' }}
                                              referrerPolicy="no-referrer"
                                            />
                                          )}
                                          {formData.platform === 'Facebook' && (item.name.toLowerCase().includes('shares') || item.name.includes('مشاركات')) && (
                                            <img 
                                              src="https://i.postimg.cc/7P1kPTMP/Screenshot-2026-05-05-221304-removebg-preview.png" 
                                              alt="emoji" 
                                              className="w-6 h-6 sm:w-9 sm:h-9 object-contain ml-1"
                                              referrerPolicy="no-referrer"
                                            />
                                          )}
                                          {formData.platform === 'Facebook' && (item.name.toLowerCase().includes('likes') || item.name.includes('Gefällt mir') || item.name.includes('إعجاب')) && (
                                            <FacebookLikesEmoji />
                                          )}
                                          {formData.platform === 'Facebook' && (item.name.toLowerCase().includes('comments') || item.name.includes('Kommentare') || item.name.includes('تعليقات')) && (
                                            <img 
                                              src="https://i.postimg.cc/1Rp2Sh0D/Gemini-Generated-Image-htv13rhtv13rhtv1-removebg-preview.png" 
                                              alt="emoji" 
                                              className="w-6 h-6 sm:w-9 sm:h-9 object-contain ml-1"
                                              referrerPolicy="no-referrer"
                                            />
                                          )}
                                        </span>
                                      </>
                                    ) : (
                                      <span>{item.name}</span>
                                    )}
                                  </div>
                                  {formData.platform === "Instagram" && item.name.toLowerCase().includes("likes") && (
                                    <img 
                                      src="https://i.postimg.cc/RVfSCzqH/insta-heart-removebg-preview.png" 
                                      alt="Likes" 
                                      className="w-5 h-5 sm:w-10 sm:h-10 object-contain ml-4 sm:ml-6"
                                      style={
                                        isMobileView 
                                          ? { width: '26px', paddingTop: '-2px', marginLeft: '20px' } 
                                          : undefined
                                      }
                                      referrerPolicy="no-referrer"
                                    />
                                  )}
                                  {formData.platform === "Instagram" && (item.name.toLowerCase().includes("followers") || item.name.toLowerCase().includes("follower") || item.name.includes("متابعين")) && (
                                    <div className="relative inline-block ml-1 sm:ml-2 w-6 h-6 sm:w-10 sm:h-10 align-middle">
                                      <img 
                                        src="https://i.postimg.cc/vH7nxDgy/pfpp.png" 
                                        alt="Followers" 
                                        className="absolute top-1/2 left-[-30px] sm:left-[-46px] -translate-y-1/2 w-28 h-28 sm:w-48 sm:h-48 object-contain max-w-none z-[100] pointer-events-none"
                                        referrerPolicy="no-referrer"
                                      />
                                    </div>
                                  )}
                                  {isMobileView && formData.platform === "TikTok" && (item.name.toLowerCase().includes("followers") || item.name.toLowerCase().includes("follower") || item.name.includes("متابعين")) && (
                                    <div className="relative inline-block ml-1 sm:ml-2 w-6 h-6 sm:w-10 sm:h-10 align-middle">
                                      <img 
                                        src="https://i.postimg.cc/vH7nxDgy/pfpp.png" 
                                        alt="Followers" 
                                        className="absolute top-1/2 left-[-35px] sm:left-[-46px] -translate-y-1/2 w-28 h-28 sm:w-48 sm:h-48 object-contain max-w-none z-[100] pointer-events-none"
                                        referrerPolicy="no-referrer"
                                      />
                                    </div>
                                  )}
                                  {formData.platform === "Instagram" && (item.name.includes("Kommentare") || item.name.includes("Comments") || item.name.includes("تعليقات")) && (
                                    <img 
                                      src="https://i.postimg.cc/PNsQG85v/commetss.png" 
                                      alt="Comments" 
                                      className="w-10 h-10 sm:w-24 sm:h-24 object-contain -ml-3 sm:-ml-8" 
                                      style={
                                        isMobileView 
                                          ? { width: '47px', paddingTop: '-1px', paddingLeft: '-1px', marginLeft: '-14px' } 
                                          : undefined
                                      }
                                      referrerPolicy="no-referrer"
                                    />
                                  )}
                                  {(item.name.includes("Views") || item.name.includes("Viewers")) && (
                                    formData.platform === "Instagram" ? (
                                      <img 
                                        src="https://i.postimg.cc/TYX1Bccw/eye-insta.png" 
                                        alt="Views" 
                                        className="w-10 h-10 sm:w-24 sm:h-24 object-contain ml-2 sm:-ml-2" 
                                        style={
                                          isMobileView 
                                            ? { paddingLeft: '-2px', marginLeft: '3px', paddingRight: '1px', marginRight: '1px', width: '50px', height: '35px', paddingTop: '-2px' } 
                                            : undefined
                                        }
                                        referrerPolicy="no-referrer"
                                      />
                                    ) : !["TikTok", "Facebook", "Telegram"].includes(formData.platform) ? (
                                      <Eye className="text-blue-400 w-3 h-3 sm:w-12 sm:h-12 ml-1 sm:ml-5" />
                                    ) : null
                                  )}
                                  {formData.platform === "Instagram" && item.name.includes("Reposts") && (
                                    <img 
                                      src="https://i.postimg.cc/T3XBgtXC/repost-insta.png" 
                                      alt="Reposts" 
                                      className="w-16 h-16 sm:w-32 sm:h-32 object-contain -ml-1 sm:-ml-1 translate-y-1 sm:translate-y-2" 
                                      style={
                                        isMobileView 
                                          ? { width: '39.5833px', height: '63px', marginLeft: '-6px', marginTop: '-1px', paddingLeft: '6px', paddingTop: '0px', paddingRight: '1px', marginRight: '-2px' } 
                                          : undefined
                                      }
                                      referrerPolicy="no-referrer"
                                    />
                                  )}

                                </div>
                              </td>
                              {headers.map((_, colIdx) => {
                                const priceKey = `p${colIdx}` as keyof typeof item;
                                const price = (item as any)[priceKey];
                                const currentCol = colIdx + 1;

                                if ((formData.platform === "Instagram" && colIdx === 0 && i < 3) || (formData.platform === "TikTok" && colIdx === 0 && (i === 0 || i === 2 || i === 3))) {
                                  return <td key={colIdx} className="py-2 px-1 sm:p-4 text-center" />;
                                }

                                return (
                                  <td 
                                    key={colIdx} 
                                    className={`py-2 px-1 sm:p-4 text-center ${
                                      tableId === "standardTable" && i === 1 && colIdx === 0 
                                        ? "w-[177px] pl-[17px] -translate-x-[4px] sm:translate-x-0" 
                                        : ""
                                    }`}
                                  >
                                    <div className="flex flex-col items-center gap-2 sm:gap-3">
                                      {((formData.platform === "TikTok" || formData.platform === "Instagram") && (item.name.includes("Kommentare") || item.name.includes("Comments") || item.name.includes("تعليقات")) && colIdx === 3) || (typeof price === 'string' && (price.includes('AUSVERKAUFT') || price.includes('SOLD OUT'))) ? (
                                        <>
                                          {!(typeof price === 'string' && (price.includes('AUSVERKAUFT') || price.includes('SOLD OUT'))) && (
                                            <span className="text-[10px] sm:text-lg font-medium text-white/90">{price}</span>
                                          )}
                                          <button 
                                            disabled
                                            className={`${
                                              formData.platform === "Facebook" && item.name.toLowerCase().includes("follower") 
                                                ? (String(headers[colIdx]).includes("250") ? "w-[130%] sm:w-full max-w-none scale-105" : "w-full")
                                                : "w-full"
                                            } h-[40px] sm:h-[64px] rounded-lg sm:rounded-xl bg-red-600/50 text-white/50 cursor-not-allowed font-bold ${
                                              formData.platform === "Facebook" && item.name.toLowerCase().includes("follower")
                                                ? (String(headers[colIdx]).includes("250") ? "text-[5px] sm:text-base" : "text-[6px] sm:text-base")
                                                : "text-[7px] sm:text-base"
                                            } uppercase tracking-tighter sm:tracking-wider border border-red-500/20 px-1 sm:px-2 flex items-center justify-center text-center`}
                                          >
                                            {tableId === 'table2' ? (
                                              <div className="flex items-center gap-1">
                                                <Lock className="w-2 h-2 sm:w-4 sm:h-4" />
                                                <span>{lang === 'de' ? 'GESPERRT' : lang === 'ar' ? 'مغلق' : 'LOCKED'}</span>
                                              </div>
                                            ) : t.modals.funnel.buttons.soldOut}
                                          </button>
                                        </>
                                      ) : (
                                        <>
                                          <span className={`text-[10px] sm:text-lg font-medium transition-all duration-300 ${activeTable === tableId && activeColumn === currentCol ? "text-white scale-105" : "text-white/90"}`}>{price}</span>
                                          <button 
                                            onMouseEnter={() => { setActiveTable(tableId); setActiveColumn(currentCol); setActiveRow(i + rowOffset); }}
                                            onMouseLeave={() => { setActiveTable(null); setActiveColumn(null); setActiveRow(null); }}
                                            onClick={() => {
                                              const isCustom = item.name.includes('Custom') || item.name.includes('selbst ausgewählte');
                                              const isCommentLike = item.name.includes('Comment Likes');
                                              const isTelegramReaction = item.name.includes('Telegram Reactions');
                                              let newPlatform = formData.platform;
                                              if (item.name.toLowerCase().includes('tiktok')) newPlatform = 'TikTok';
                                              else if (item.name.toLowerCase().includes('instagram')) newPlatform = 'Instagram';
                                              else if (item.name.toLowerCase().includes('facebook')) newPlatform = 'Facebook';
                                              
                                              const isFacebookLike = formData.platform === 'Facebook' && (item.name === 'Facebook Likes' || item.name === 'Facebook Gefällt mir' || (typeof item.name === 'string' && item.name.includes('إعجاب')));
                                              
                                              // Determine number of comments based on column quantity
                                              let commentCount = 10;
                                              if (isCustom) {
                                                const qty = headers[colIdx];
                                                if (typeof qty === 'number') commentCount = qty;
                                                else {
                                                  const match = String(qty).match(/\d+/);
                                                  if (match) commentCount = parseInt(match[0]);
                                                }
                                              }

                                              setFormData({ 
                                                ...formData, 
                                                platform: newPlatform,
                                                goal: `${item.name} (${headers[colIdx]}) - ${price}`, 
                                                selectedServiceName: item.name.includes('<') ? item.name.replace(/<[^>]*>/g, '') : item.name,
                                                selectedQuantity: String(headers[colIdx]),
                                                selectedItem: {
                                                  amount: String(headers[colIdx]),
                                                  name: item.name.includes('<') ? item.name.replace(/<[^>]*>/g, '') : item.name,
                                                  price: price
                                                },
                                                isCustom,
                                                isCommentLike,
                                                isTelegramReaction,
                                                isFacebookLike,
                                                customComments: Array(commentCount).fill("")
                                              });
                                              handleNext();
                                            }}
                                            className={`w-full h-[40px] sm:h-[64px] rounded-lg sm:rounded-xl transition-all font-bold ${lang === 'ar' ? 'text-[12px] sm:text-base' : 'text-[9px] sm:text-sm'} uppercase tracking-tight sm:tracking-wider relative overflow-hidden group/btn-glass flex items-center justify-center text-center p-1 sm:p-2 ${
                                              isFreeTrial && formData.platform === "Instagram" && item.name.toLowerCase().includes("views") && colIdx === 1
                                                ? "bg-blue-500 text-white animate-pulse shadow-[0_0_20px_rgba(59,130,246,0.8)] border-2 border-blue-400"
                                                : formData.platform === "Google Maps"
                                                  ? "bg-blue-700 hover:bg-blue-600 border border-blue-400/30 text-white shadow-[0_0_15px_rgba(29,78,216,0.4)]"
                                                  : "bg-blue-600 hover:bg-blue-500 text-white"
                                            }`}
                                          >
                                            {isFreeTrial && formData.platform === "Instagram" && item.name.toLowerCase().includes("views") && colIdx === 1 ? (
                                              lang === 'de' ? 'KOSTENLOS' : lang === 'ar' ? 'مجاناً' : 'FREE'
                                            ) : formData.platform === 'Facebook' && (item.name === 'Facebook Likes' || item.name === 'Facebook Gefällt mir' || (typeof item.name === 'string' && item.name.includes('إعجاب'))) ? (
                                              lang === 'de' ? 'JETZT ANPASSEN' : lang === 'ar' ? 'تخصيص' : <span className="text-[7px] sm:text-sm">PERSONALISE</span>
                                            ) : (
                                              <>
                                                {formData.platform === "Google Maps" && (
                                                  <div className="absolute inset-0 bg-gradient-to-tr from-white/10 to-transparent opacity-0 group-hover/btn-glass:opacity-100 transition-opacity pointer-events-none" />
                                                )}
                                                {customButtonText || t.modals.funnel.buttons.buy}
                                              </>
                                            )}
                                          </button>
                                        </>
                                      )}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    );

                    const renderRGBWrapped = (content: React.ReactNode, key: string, mtClass = "mt-8 sm:mt-12") => (
                      <div key={key} className={`relative ${mtClass} mx-auto max-w-full sm:max-w-full p-[2px] sm:p-[4px]`}>
                        {/* Continuous Wrapped RGB LED Border */}
                        <div className="absolute inset-0 z-0 pointer-events-none rounded-[1.25rem] sm:rounded-[2.25rem] overflow-hidden">
                          <motion.div
                            animate={{ rotate: 360 }}
                            transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                            className="absolute inset-[-500%] bg-[conic-gradient(from_0deg,#ff0000,#ff7f00,#ffff00,#00ff00,#00ffff,#0000ff,#8b00ff,#ff0000,#ff0000)]"
                          />
                        </div>
                        <div className="absolute inset-[1.5px] sm:inset-[2.5px] z-[1] bg-[#050505] rounded-[1.2rem] sm:rounded-[2.2rem]" />
                        <div className="relative z-10 overflow-x-auto liquid-glass rounded-[1.1rem] sm:rounded-[2.1rem] border border-white/20 bg-[#0a0f1e]/80 shadow-2xl scrollbar-hide">
                          {content}
                        </div>
                      </div>
                    );

                    if (["Google Maps", "TikTok", "Instagram", "Facebook", "Telegram"].includes(formData.platform)) {
                      const table1Rows = [
                        { name: 'TikTok video Comment Likes', p0: "1,60 €", p1: "2,20 €", p2: "3,50 €", p3: "4,80 €" },
                        { name: 'Instagram Video Comment Likes', p0: "1,60 €", p1: "2,20 €", p2: "3,50 €", p3: "4,80 €" },
                      ];
                      
                      const allTable2Rows = [
                        { name: (lang === 'de' ? 'selbst ausgewählte' : lang === 'ar' ? 'فيديو تيك توك مخصص' : 'Custom') + ` TikTok video ${lang === 'de' ? 'Kommentare' : lang === 'ar' ? 'تعليقات' : 'Comments'}`, p0: "1,90 €", p1: "3,50 €", p2: "4,90 €" },
                        { name: (lang === 'de' ? 'selbst ausgewählte' : lang === 'ar' ? 'فيديو انستغرام مخصص' : 'Custom') + ` instagram Video/Post ${lang === 'de' ? 'Kommentare' : lang === 'ar' ? 'تعليقات' : 'Comments'}`, p0: "1,90 €", p1: "3,50 €", p2: "4,90 €" },
                        { name: (lang === 'de' ? 'selbst ausgewählte' : lang === 'ar' ? 'منشور فيسبوك مخصص' : 'Custom') + ` Facebook Video/Post ${lang === 'de' ? 'Kommentare' : lang === 'ar' ? 'تعليقات' : 'Comments'}`, p0: "1,90 €", p1: "3,50 €", p2: "4,90 €" },
                      ];

                      // For main platforms, show their specific custom row. For Google Maps, show all.
                      const table2Rows = formData.platform === "Google Maps" 
                        ? allTable2Rows 
                        : allTable2Rows.filter(row => row.name.toLowerCase().includes(formData.platform.toLowerCase()));

                      const standardItems = [
                        { name: formData.platform === 'Instagram' ? 'Instagram Likes' : (formData.platform === 'Telegram' ? 'Telegram Reactions' : `${formData.platform} Likes`), 
                          p0: formData.platform === 'Facebook' ? "0,90 €" : "0,70 €", 
                          p1: formData.platform === 'Facebook' ? "2,30 €" : (formData.platform === 'TikTok' ? "1,90 €" : "1,70 €"), 
                          p2: formData.platform === 'Facebook' ? "3,50 €" : (formData.platform === 'TikTok' ? "2,80 €" : "2,70 €"), 
                          p3: formData.platform === 'Facebook' ? "4,20 €" : (formData.platform === 'TikTok' ? "4,30 €" : "3,40 €") 
                        },
                        { name: formData.platform === 'Telegram' ? (lang === 'ar' ? 'أعضاء تيليجرام' : 'Telegram Members') : `${formData.platform} ${lang === 'en' ? 'Followers' : lang === 'ar' ? 'متابعين' : 'Follower'}`, 
                          p0: formData.platform === 'Facebook' ? (lang === 'de' ? 'AUSVERKAUFT' : 'SOLD OUT') : (formData.platform === 'TikTok' ? "3 €" : "3,50 €"), 
                          p1: formData.platform === 'Facebook' ? (lang === 'de' ? 'AUSVERKAUFT' : 'SOLD OUT') : (formData.platform === 'TikTok' ? "6,40 €" : "6,50 €"), 
                          p2: formData.platform === 'Facebook' ? (lang === 'de' ? 'AUSVERKAUFT' : 'SOLD OUT') : "8,90 €", 
                          p3: formData.platform === 'Facebook' ? (lang === 'de' ? 'AUSVERKAUFT' : 'SOLD OUT') : "14,99 €" 
                        },
                        ...(formData.platform !== 'Facebook' ? [{ name: `${formData.platform} ${lang === 'ar' ? 'مشاهدات' : 'Views'}`, p0: "2,99 €", p1: formData.platform === 'TikTok' ? "1,50 €" : "1 €", p2: formData.platform === 'Instagram' ? "1,90 €" : "2,20 €", p3: "3,80 €" }] : []),
                        { name: `${formData.platform} ${lang === 'de' ? 'Kommentare' : lang === 'ar' ? 'تعليقات' : 'Comments'}`, p0: formData.platform === 'Facebook' ? "2,00 €" : "2,20 €", p1: "4,50 €", p2: "6,10 €", p3: "8,80 €" },
                        { 
                          name: formData.platform === 'TikTok' 
                            ? (lang === 'ar' ? 'حفظ TikTok' : 'TikTok Saves') 
                            : formData.platform === 'Facebook'
                              ? (lang === 'ar' ? 'مشاركات Facebook' : 'Facebook Shares')
                              : `${formData.platform} ${lang === 'ar' ? 'إعادة نشر' : 'Reposts'}`, 
                          p0: "1,70 €", 
                          p1: "3,20 €", 
                          p2: "4 €", 
                          p3: "9,20 €" 
                        },
                        ...(formData.platform === 'TikTok' ? [{ name: lang === 'ar' ? 'مشاركات TikTok' : 'TikTok Shares', p0: "1,40 €", p1: "2,50 €", p2: "3,80 €", p3: "8,50 €" }] : []),
                      ];

                      return (
                        <div className="flex flex-col gap-6 sm:gap-10 pb-8 sm:pb-16">
                          {/* Only show Comment Likes table for Google Maps */}
                          {formData.platform === "Google Maps" && (
                            renderRGBWrapped(renderTable(
                              table1Rows, 
                              [250, 500, "1k", "3k"], 
                              "table1", 
                              3, 
                              false, 
                              undefined, 
                              undefined, 
                              lang === 'de' ? 'Meinungen verändern' : lang === 'ar' ? 'تغيير الآراء' : 'CONTROL OPINIONS'
                            ), "table1", "mt-6 sm:mt-8")
                          )}

                          {formData.platform === "Google Maps" && table2Rows.length > 0 && renderRGBWrapped(renderTable(table2Rows, [10, 20, 40], "table2", 0, true, (t.modals.funnel.buttons as any).customise || t.modals.funnel.buttons.buy, (t.modals.funnel.buttons as any).commentsSuffix, lang === 'de' ? 'Personalisieren !' : lang === 'ar' ? 'تخصيص !' : 'CONTROL THE NARRATIVE'), "table2", "mt-6 sm:mt-8")}

                          {formData.platform !== "Google Maps" && (
                            <div className="mt-8 sm:mt-12 overflow-x-auto liquid-glass rounded-2xl border border-white/20 bg-[#0a0f1e]/80 shadow-2xl mx-1 sm:mx-0">
                              {renderTable(standardItems, [250, "1K", "2K", "5K"], "standardTable")}
                            </div>
                          )}
                        </div>
                      );
                    }
                  }

                  return (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {goalsByPlatform[formData.platform]?.map((g) => (
                        <button
                          key={g}
                          onClick={() => {
                            setFormData({ ...formData, goal: g });
                            handleNext();
                          }}
                          className={`p-4 rounded-xl text-center font-bold transition-all flex items-center justify-center text-sm ${
                            formData.goal === g ? "bg-blue-600 text-white" : "glass hover:bg-white/10"
                          }`}
                        >
                          {g}
                        </button>
                      ))}
                    </div>
                  );
                })()}

                <button onClick={onClose} className="mt-8 text-white/50 font-bold hover:text-white transition-colors">
                  {t.modals.funnel.buttons.back}
                </button>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="text-center">
                {formData.isTelegramReaction ? (
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_auto_0.8fr] items-start lg:items-center gap-8 lg:gap-12 max-w-6xl mx-auto w-full">
                    <div className="order-2 lg:order-1 text-left max-w-xl mx-auto lg:ml-0 w-full">
                      <h2 className="text-xl sm:text-2xl font-black mb-8 text-white tracking-tight text-center lg:text-left">
                        {(t.modals.funnel.buttons as any).customStep?.reactionQuestion}
                      </h2>

                      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-3 gap-3 mb-8">
                        {[
                          { emoji: "❤️‍🔥", label: lang === 'de' ? 'Herz auf Feuer' : 'Heart on fire' },
                          { emoji: "❤️", label: lang === 'de' ? 'Herz' : 'Heart' },
                          { emoji: "💘", label: lang === 'de' ? 'Hingabe' : 'Love struck' },
                          { emoji: "💔", label: lang === 'de' ? 'Gebrochenes Herz' : 'Broken heart' },
                          { emoji: "🔥", label: lang === 'de' ? 'Feuer' : 'Fire' },
                          { emoji: "👍", label: lang === 'de' ? 'Daumen hoch' : 'Thumbs up' },
                          { emoji: "👎", label: lang === 'de' ? 'Daumen runter' : 'Thumbs down' },
                          { emoji: "💩", label: lang === 'de' ? 'Kacke' : 'Poop' },
                          { emoji: "⚡", label: lang === 'de' ? 'Blitz' : 'Lightning' }
                        ].map((reaction) => (
                          <button
                            key={reaction.label}
                            onClick={() => setFormData({ ...formData, selectedReaction: reaction.emoji })}
                            className={`flex flex-col items-center justify-center gap-2 p-3 rounded-xl transition-all border ${
                              formData.selectedReaction === reaction.emoji
                                ? "bg-blue-600/20 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                                : "bg-white/5 border-white/10 hover:bg-white/10"
                            }`}
                          >
                            <span className="text-2xl sm:text-3xl">{reaction.emoji}</span>
                            <span className="text-[10px] font-bold text-white/40 uppercase tracking-tighter truncate w-full">
                              {reaction.label}
                            </span>
                          </button>
                        ))}
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-4 mb-1 ml-1">
                          <label className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-widest block">
                            {urlLabel}
                          </label>
                          <Info size={16} className="text-white/30" />
                        </div>
                        <input
                          type="text"
                          placeholder={t.modals.funnel.placeholders.url}
                          value={formData.url}
                          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                          className="w-full glass p-4 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-white font-medium bg-white/5"
                        />
                      </div>
                    </div>
                    {/* Vertical Divider (Desktop Only) */}
                    <div className="hidden lg:block w-px h-64 bg-transparent" />
                    {/* Right Column Summary */}
                    <div className="order-1 lg:order-2 flex flex-col items-center lg:items-end justify-center text-center lg:text-right w-full">
                      <SummaryDisplay formData={formData} lang={lang} />
                      {formData.selectedReaction && (
                        <div className="mt-4 flex items-center justify-center lg:justify-end gap-3 bg-white/5 p-3 rounded-2xl border border-white/10">
                          <span className="text-3xl">{formData.selectedReaction}</span>
                          <span className="text-xs font-bold text-white/60 uppercase tracking-widest">{lang === 'de' ? 'Gewählt' : 'Selected'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : formData.isFacebookLike ? (
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_auto_0.8fr] items-start lg:items-center gap-8 lg:gap-12 max-w-6xl mx-auto w-full">
                    <div className="order-2 lg:order-1 text-left max-w-xl mx-auto lg:ml-0 w-full">
                      <h2 className="text-xl sm:text-2xl font-black mb-8 text-white tracking-tight text-center lg:text-left">
                        {lang === 'de' ? 'Welche Reaktion möchtest du?' : lang === 'ar' ? 'أي تفاعل تريد؟' : 'Which reaction do you want?'}
                      </h2>

                      <div className="grid grid-cols-3 sm:grid-cols-5 lg:grid-cols-3 gap-3 mb-8">
                        {[
                          "https://i.postimg.cc/QxWthqxB/Gemini-Generateddada-Image-1j0vym1j0vym1j0v-removebg-preview-1-removebg-preview(1).png",
                          "https://i.postimg.cc/V6CfmfmX/Screenshot-2026-05-0dd6-001107-removebg-preview(3).png",
                          "https://i.postimg.cc/RCgCcDqm/Untitlsed-removebg-preview.png",
                          "https://i.postimg.cc/76gVFzGc/Unthhitled-removebg-preview.png",
                          "https://i.postimg.cc/J7FV7VGF/22Untitled-removebg-preview.png"
                        ].map((imgSrc, idx) => (
                          <button
                            key={idx}
                            onClick={() => setFormData({ ...formData, selectedFacebookEmoji: imgSrc })}
                            className={`flex flex-col items-center justify-center p-3 rounded-xl transition-all border ${
                              formData.selectedFacebookEmoji === imgSrc
                                ? "bg-blue-600/20 border-blue-500 shadow-[0_0_15px_rgba(59,130,246,0.3)]"
                                : "bg-white/5 border-white/10 hover:bg-white/10"
                            }`}
                          >
                            <img src={imgSrc} alt="reaction" className="w-12 h-12 sm:w-16 sm:h-16 object-contain" referrerPolicy="no-referrer" />
                          </button>
                        ))}
                      </div>

                      <div className="space-y-3">
                        <div className="flex items-center gap-4 mb-1 ml-1">
                          <label className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-widest block">
                            {urlLabel}
                          </label>
                          <Info size={16} className="text-white/30" />
                        </div>
                        <input
                          type="text"
                          placeholder={t.modals.funnel.placeholders.url}
                          value={formData.url}
                          onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                          className="w-full glass p-4 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-white font-medium bg-white/5"
                        />
                      </div>
                    </div>
                    <div className="hidden lg:block w-px h-64 bg-transparent" />
                    <div className="order-1 lg:order-2 flex flex-col items-center lg:items-end justify-center text-center lg:text-right w-full">
                      <SummaryDisplay formData={formData} lang={lang} />
                      {formData.selectedFacebookEmoji && (
                        <div className="mt-4 flex items-center justify-center lg:justify-end gap-3 bg-white/5 p-3 rounded-2xl border border-white/10">
                          <img src={formData.selectedFacebookEmoji} alt="selected" className="w-10 h-10 object-contain" referrerPolicy="no-referrer" />
                          <span className="text-xs font-bold text-white/60 uppercase tracking-widest">{lang === 'de' ? 'Gewählt' : 'Selected'}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ) : formData.isCommentLike ? (
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_auto_0.8fr] items-start lg:items-center gap-8 lg:gap-12 max-w-6xl mx-auto w-full">
                    <div className="order-2 lg:order-1 text-left max-w-xl mx-auto lg:ml-0 w-full">
                      <h2 className="text-xl sm:text-2xl font-black mb-8 text-white tracking-tight text-center lg:text-left">
                        {(t.modals.funnel.buttons as any).customStep?.commentLikesQuestion}
                      </h2>

                      <div className="space-y-8">
                        <div className="space-y-3">
                          <label className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-widest block ml-1">
                            {t.modals.funnel.labels.commenterUsername}
                          </label>
                          <input
                            type="text"
                            placeholder={t.modals.funnel.placeholders.commenterUsername}
                            value={formData.commenterUsername}
                            onChange={(e) => setFormData({ ...formData, commenterUsername: e.target.value })}
                            className="w-full glass p-4 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-white font-medium bg-white/5"
                          />
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center gap-4 mb-1 ml-1">
                            <label className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-widest block">
                              {urlLabel}
                            </label>
                            <Info size={16} className="text-white/30" />
                          </div>
                          <input
                            type="text"
                            placeholder={t.modals.funnel.placeholders.url}
                            value={formData.url}
                            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                            className="w-full glass p-4 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-white font-medium bg-white/5"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Vertical Divider (Desktop Only) */}
                    <div className="hidden lg:block w-px h-64 bg-transparent" />
                    {/* Right Column Summary */}
                    <div className="order-1 lg:order-2 flex flex-col items-center lg:items-end justify-center text-center lg:text-right w-full">
                      <SummaryDisplay formData={formData} lang={lang} />
                    </div>
                  </div>
                ) : formData.isCustom ? (
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_auto_0.8fr] items-start lg:items-center gap-8 lg:gap-12 max-w-6xl mx-auto w-full">
                    <div className="order-2 lg:order-1 text-left max-w-xl mx-auto lg:ml-0 w-full">
                      <h2 className="text-xl sm:text-2xl font-black mb-6 text-white tracking-tight text-center lg:text-left">
                        {(t.modals.funnel.buttons as any).customStep?.question}
                      </h2>
                      
                      <div className="grid grid-cols-1 gap-2 mb-8 max-h-[250px] overflow-y-auto pr-2 custom-scrollbar">
                        {formData.customComments.map((comment, idx) => (
                          <div key={idx} className="relative group flex items-center gap-2 sm:gap-4">
                            <span className="w-5 sm:w-6 text-right text-[10px] sm:text-xs font-mono font-bold text-white/20 group-hover:text-white/40 transition-colors shrink-0">
                              {String(idx + 1).padStart(2, '0')}
                            </span>
                            <input
                              type="text"
                              placeholder={(t.modals.funnel.buttons as any).customStep?.placeholder}
                              value={comment}
                              onChange={(e) => {
                                const newComments = [...formData.customComments];
                                newComments[idx] = e.target.value;
                                setFormData({ ...formData, customComments: newComments });
                              }}
                              className="flex-grow bg-white/5 border border-white/10 p-3 sm:p-4 rounded-xl focus:outline-none focus:border-blue-500/50 transition-all text-sm hover:bg-white/10 text-white"
                            />
                          </div>
                        ))}
                      </div>

                      <div className="mt-10 pt-10 border-t border-white/10">
                          <h3 className="text-lg sm:text-xl font-bold mb-4 text-white/90 text-center lg:text-left">
                            {formData.platform === 'TikTok' 
                              ? 'TikTok' 
                              : formData.platform === 'Instagram' 
                                ? 'Instagram' 
                                : formData.platform === 'Facebook'
                                  ? 'Facebook'
                                  : formData.platform}{" "}
                            {isAccountRelated 
                              ? (lang === 'de' ? 'Konto-Link hinzufügen' : lang === 'ar' ? 'أضف رابط الحساب' : 'Add account link')
                              : (t.modals.funnel.buttons as any).customStep?.videoQuestion}
                          </h3>
                          <div className="space-y-4">
                            <div className="flex items-center justify-center lg:justify-start gap-4 mb-3">
                              <label className="text-[10px] sm:text-xs font-bold text-white/40 uppercase tracking-widest block">
                                {isAccountRelated 
                                  ? (lang === 'de' ? 'Konto Link eingeben' : lang === 'ar' ? 'أدخل رابط الحساب' : 'enter account link')
                                  : (lang === 'de' ? 'video link eingeben' : lang === 'ar' ? 'أدخل رابط الفيديو' : 'enter video link')}
                              </label>
                            </div>
                          <input
                            type="text"
                            placeholder={t.modals.funnel.placeholders.url}
                            value={formData.url}
                            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                            className="w-full glass p-4 rounded-xl focus:outline-none focus:border-blue-500 transition-colors text-center text-sm sm:text-lg font-medium bg-white/5 text-white"
                          />
                        </div>
                      </div>
                    </div>
                    {/* Vertical Divider (Desktop Only) */}
                    <div className="hidden lg:block w-px h-64 bg-transparent" />
                    {/* Right Column Summary */}
                    <div className="order-1 lg:order-2 flex flex-col items-center lg:items-end justify-center text-center lg:text-right w-full">
                      <SummaryDisplay formData={formData} lang={lang} />
                    </div>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-[1.2fr_auto_0.8fr] items-center gap-8 lg:gap-12 max-w-6xl mx-auto w-full">
                    {/* Left Column: Form Inputs */}
                    <div className="order-2 lg:order-1 text-left max-w-md lg:max-w-2xl mx-auto lg:ml-0 w-full">
                      <h2 className="text-2xl sm:text-4xl font-black mb-6 tracking-tight text-center lg:text-left">{t.modals.funnel.step3}</h2>
                      <div className="space-y-6">
                        <div className="space-y-3">
                          <div className="flex items-center justify-center lg:justify-start gap-3 ml-1">
                            <label className="text-[10px] sm:text-sm font-bold text-white/50 uppercase tracking-widest block">{urlLabel}</label>
                            <Info size={16} className="text-white/30" />
                          </div>
                          <input 
                            type="text" 
                            placeholder={t.modals.funnel.placeholders.url}
                            className="w-full glass p-4 sm:p-6 rounded-xl sm:rounded-2xl focus:outline-none focus:border-blue-500 transition-colors text-center sm:text-left text-lg sm:text-xl font-medium bg-white/5 text-white"
                            value={formData.url}
                            onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Vertical Divider (Desktop Only) */}
                    <div className="hidden lg:block w-px h-64 bg-transparent" />

                    {/* Right Column: Information Summary */}
                    <div className="order-1 lg:order-2 flex flex-col items-center lg:items-end justify-center text-center lg:text-right w-full">
                      <SummaryDisplay formData={formData} lang={lang} />
                    </div>
                  </div>
                )}
                
                <div className="flex flex-row items-center justify-center gap-4 sm:gap-8 mt-6 sm:mt-12 pb-8">
                  <button onClick={handleBack} className="text-white/50 font-bold hover:text-white transition-colors shrink-0">
                    {t.modals.funnel.buttons.back}
                  </button>
                  <button 
                    onClick={handleNext}
                    disabled={!formData.url || (formData.isCommentLike && !formData.commenterUsername) || (formData.isTelegramReaction && !formData.selectedReaction)}
                    className={`flex-grow md:flex-initial max-w-[200px] md:w-[300px] p-4 sm:p-5 rounded-xl sm:rounded-2xl font-bold transition-all text-sm sm:text-base relative overflow-hidden group/btn-glass ${
                      formData.platform === "Google Maps"
                        ? "liquid-glass backdrop-blur-xl border border-white/20 text-white shadow-xl hover:bg-white/10 disabled:opacity-30"
                        : "bg-blue-600 hover:bg-blue-500 disabled:opacity-50 disabled:hover:bg-blue-600 text-white"
                    }`}
                  >
                    {formData.platform === "Google Maps" && (
                      <div className="absolute inset-0 bg-gradient-to-tr from-white/15 to-transparent opacity-0 group-hover/btn-glass:opacity-100 transition-opacity pointer-events-none" />
                    )}
                    {formData.goal.includes('€') ? (t.modals.funnel.buttons as any).next : t.modals.funnel.buttons.request}
                  </button>
                </div>
              </motion.div>
            )}

            {step === 4 && (
              <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center relative">
                <div className="flex items-center justify-center relative mb-4 sm:mb-8">
                  <button 
                    onClick={handleBack} 
                    className="absolute left-0 text-white/50 font-bold hover:text-white transition-colors text-xs sm:text-base flex items-center gap-1 -translate-y-4 sm:-translate-y-2"
                  >
                    <ChevronRight size={16} className="rotate-180" />
                    {t.modals.funnel.buttons.back}
                  </button>
                  <h2 className="text-2xl sm:text-4xl font-black tracking-tight">{(t.modals.funnel as any).payment?.title}</h2>
                </div>
                <p className="text-white/60 mb-8 sm:mb-10 text-sm sm:text-lg">{(t.modals.funnel as any).payment?.subtitle}</p>
                
                {/* Order Summary */}
                <div className="bg-white/5 rounded-2xl px-6 pt-6 pb-2 mb-8 max-w-md mx-auto border border-white/10 relative group/product">
                  {(formData.platform === "Instagram" || formData.platform === "TikTok") && formData.selectedItem?.name.toLowerCase().includes("likes") && 
                   (formData.selectedItem?.amount === "1K" || formData.selectedItem?.amount === "2K" || formData.selectedItem?.amount === "5K") && (
                    <FloatingHearts />
                  )}
                  <div className="flex flex-col items-center gap-2 relative z-10">
                    <span className="text-4xl sm:text-5xl font-black text-white">{formData.selectedItem?.amount}</span>
                    {formData.platform === "Instagram" && (formData.selectedItem?.name.toLowerCase().includes("follower") || formData.selectedItem?.name.toLowerCase().includes("followers")) ? (
                      <img 
                        src="https://i.postimg.cc/NGJ9qsys/insta-follo22w2wers-removebg-preview.png" 
                        alt="Instagram Followers"
                        className="h-32 sm:w-[372px] sm:h-auto object-contain scale-125"
                        referrerPolicy="no-referrer"
                      />
                    ) : formData.platform === "Instagram" && (formData.selectedItem?.name.toLowerCase().includes("views") || formData.selectedItem?.name.toLowerCase().includes("viewers") || formData.selectedItem?.name.includes("مشاهدات")) ? (
                      <img 
                        src="https://i.postimg.cc/TYX1Bccw/eye-insta.png" 
                        alt="Instagram Views"
                        className="h-32 sm:h-40 object-contain scale-125"
                        referrerPolicy="no-referrer"
                      />
                    ) : formData.platform === "Instagram" && (formData.selectedItem?.name.toLowerCase().includes("kommentare") || formData.selectedItem?.name.toLowerCase().includes("comments") || formData.selectedItem?.name.includes("تعليقات")) ? (
                      <img 
                        src="https://i.postimg.cc/PNsQG85v/commetss.png" 
                        alt="Instagram Comments"
                        className="h-32 sm:h-40 object-contain scale-125"
                        referrerPolicy="no-referrer"
                      />
                    ) : formData.platform === "Instagram" && (formData.selectedItem?.name.toLowerCase().includes("reposts") || formData.selectedItem?.name.includes("إعادة نشر")) ? (
                      <img 
                        src="https://i.postimg.cc/T3XBgtXC/repost-insta.png" 
                        alt="Instagram Reposts"
                        className="h-32 sm:h-40 object-contain scale-125"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <span className="text-xl sm:text-2xl font-bold text-white/90">{formData.selectedItem?.name}</span>
                    )}
                  </div>
                  <div className="mt-4 pb-4 border-t border-white/5 flex flex-col items-center">
                    <div className="relative inline-block">
                      <span className="text-3xl font-black text-white/90 tracking-tight">{formData.selectedItem?.price}</span>
                      {isFreeTrial && isPaypalViewers1K && showRedLine && (
                        <motion.div 
                          initial={{ scaleX: 0 }}
                          animate={{ scaleX: 1 }}
                          transition={{ duration: 0.5, ease: "circOut" }}
                          className="absolute top-1/2 left-0 w-full h-[4px] bg-red-600 z-[9999] shadow-[0_0_15px_rgba(220,38,38,1)] origin-left pointer-events-none -rotate-2"
                          style={{ top: 'calc(50% - 2px)' }}
                        />
                      )}
                    </div>
                    {isFreeTrial && isPaypalViewers1K && showFreeSubmission && (
                      <motion.span 
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="text-blue-400 font-black text-xl mt-1 tracking-wider"
                      >
                        {lang === 'de' ? 'KOSTENLOS' : lang === 'ar' ? 'مجاناً' : 'FREE'}
                      </motion.span>
                    )}
                  </div>
                </div>
                
                <div className="max-w-md mx-auto space-y-4">
                  {isPaypalItem && !(isFreeTrial && isPaypalViewers1K && showFreeSubmission) ? (
                    <div className="w-full relative">
                      {isSubmitting && (
                        <div className="absolute inset-0 z-[100] bg-black/60 backdrop-blur-sm flex flex-col items-center justify-center rounded-2xl">
                          <div className="w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mb-4" />
                          <p className="font-bold text-white">Spawning Order...</p>
                        </div>
                      )}
                      
                      {/* PayPal Skeleton Loader */}
                      <AnimatePresence>
                        {(!isPaymentReady && !isSubmitting) && (
                          <motion.div 
                            initial={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.3 }}
                            className="absolute inset-0 z-20 pointer-events-none"
                          >
                            <div className="w-full h-[150px] rounded-2xl bg-white/[0.03] border border-white/10 shimmer flex flex-col items-center justify-center gap-3">
                              <div className="w-8 h-8 rounded-full border-2 border-white/20 border-t-white/60 animate-spin" />
                              <span className="text-[10px] font-bold text-white/30 uppercase tracking-[0.2em]">Preparing Payment Gateway</span>
                            </div>
                          </motion.div>
                        )}
                      </AnimatePresence>

                      <motion.div 
                        animate={{ 
                          opacity: isPaymentReady ? 1 : 0,
                          y: isPaymentReady ? 0 : 5
                        }}
                        transition={{ duration: 0.4 }}
                        className="w-full min-h-[150px]"
                      >
                        {isPaypalItem1K && <div id="paypal-container-X8KDUQT9THV7Q" className="w-full min-h-[150px]"></div>}
                        {isPaypalItem2K && <div id="paypal-container-2G3DDZLGM6DWA" className="w-full min-h-[150px]"></div>}
                        {isPaypalItem5K && <div id="paypal-container-WTSWWJW4TUF4U" className="w-full min-h-[150px]"></div>}
                        {isPaypalFollowers1K && <div id="paypal-container-8MFB5LNR6KED6" className="w-full min-h-[150px]"></div>}
                        {isPaypalFollowers2K && <div id="paypal-container-E5VCGSTP4EWQJ" className="w-full min-h-[150px]"></div>}
                        {isPaypalFollowers5K && <div id="paypal-container-REAZUDEBCRH4C" className="w-full min-h-[150px]"></div>}
                        {isPaypalViewers1K && <div id="paypal-container-LKPWQ2A95ZDJC" className="w-full min-h-[150px]"></div>}
                        {isPaypalViewers2K && <div id="paypal-container-5G93AU2U9U376" className="w-full min-h-[150px]"></div>}
                        {isPaypalViewers5K && <div id="paypal-container-538LSAPUH4D72" className="w-full min-h-[150px]"></div>}
                        {isPaypalComments250 && <div id="paypal-container-MSJT5YYVSS2WE" className="w-full min-h-[150px]"></div>}
                        {isPaypalComments1K && <div id="paypal-container-3DD4322Y8QNW6" className="w-full min-h-[150px]"></div>}
                        {isPaypalComments2K && <div id="paypal-container-V6ND3T6J4SYK8" className="w-full min-h-[150px]"></div>}
                        {isPaypalReposts250 && <div id="paypal-container-K56UCNZ7XVZ7C" className="w-full min-h-[150px]"></div>}
                        {isPaypalReposts1K && <div id="paypal-container-AVF5RJZDBD9HG" className="w-full min-h-[150px]"></div>}
                        {isPaypalReposts2K && <div id="paypal-container-AVF5RJZDBD9HG" className="w-full min-h-[150px]"></div>}
                        {isPaypalReposts5K && <div id="paypal-container-C97JWTYX5PMFY" className="w-full min-h-[150px]"></div>}
                        {isPaypalTikTokLikes && <div id="paypal-container-3J39D4FFH9A3L" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokFollowers250 && <div id="paypal-container-7MYGBSLD87W86" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokFollowers1K && <div id="paypal-container-R87HPPMKZX33S" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokFollowers2K && <div id="paypal-container-ZCB56DJJ8S3SJ" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokFollowers5K && <div id="paypal-container-74JLMHECY9KQG" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokViews1K && <div id="paypal-container-4NY4UR8UXZ4FC" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokViews2K && <div id="paypal-container-4PWERUW8UETQL" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokViews5K && <div id="paypal-container-E2YMHA6WPFHFG" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokComments1K && <div id="paypal-container-S3ST3SGQUZEPA" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokComments2K && <div id="paypal-container-M2LT7ED7FLBQN" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokSaves250 && <div id="paypal-container-KKE3M6V5K43NN" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokSaves1K && <div id="paypal-container-GCX3V82UPU42A" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokSaves2K && <div id="paypal-container-PPRCSS889V3ZQ" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokSaves5K && <div id="paypal-container-SKT72752QVZWU" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokShares250 && <div id="paypal-container-FGN6XE8B58NPE" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokShares1K && <div id="paypal-container-FSBRBFYR3VZSQ" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokShares2K && <div id="paypal-container-6TQ3KNCHLH2PW" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokShares5K && <div id="paypal-container-7VTBDYGNBXPVW" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookLikes250 && <div id="paypal-container-53L37KANNPC4W" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookLikes1K && <div id="paypal-container-R4D3C9G7QPVE8" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookLikes2K && <div id="paypal-container-CGR3YL6WLWE9W" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookLikes5K && <div id="paypal-container-7U476TWUZUM3C" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookComments250 && <div id="paypal-container-9B4FQMPVGW69L" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookComments1K && <div id="paypal-container-CP2PUPXUQW582" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookComments2K && <div id="paypal-container-DWYCXQMGWD8X4" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookComments5K && <div id="paypal-container-ATH9MZJQPY9XE" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookShares250 && <div id="paypal-container-5UUKUWXV3F4FW" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookShares1K && <div id="paypal-container-X5Q83ENVAS3WE" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookShares2K && <div id="paypal-container-6TJ2JA6AAZDPU" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookShares5K && <div id="paypal-container-EAYALQGRHPUNC" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokCommentLikes250 && <div id="paypal-container-Y9CQLUG7LXASA" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokCommentLikes500 && <div id="paypal-container-QTYDQFR9KUX8C" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokCommentLikes1K && <div id="paypal-container-TYXS2WMDJC24U" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokCommentLikes3K && <div id="paypal-container-9DZHHJU57HKF2" className="w-full min-h-[150px]" />}
                        {isPaypalInstagramCommentLikes250 && <div id="paypal-container-4CUF8FJ5Y2TTN" className="w-full min-h-[150px]" />}
                        {isPaypalInstagramCommentLikes500 && <div id="paypal-container-FG8AV2H5LJ2XS" className="w-full min-h-[150px]" />}
                        {isPaypalInstagramCommentLikes1K && <div id="paypal-container-ZGWKWC99HYG5C" className="w-full min-h-[150px]" />}
                        {isPaypalInstagramCommentLikes2K && <div id="paypal-container-Y4FPTHLVCPMT8" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokCustomComments10 && <div id="paypal-container-6DFBU9CTKD8VQ" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokCustomComments20 && <div id="paypal-container-ABGAK8EG6A8GC" className="w-full min-h-[150px]" />}
                        {isPaypalTikTokCustomComments40 && <div id="paypal-container-VVKLU6QZZBNMG" className="w-full min-h-[150px]" />}
                        {isPaypalInstagramCustomComments10 && <div id="paypal-container-8RZXCJ8Q5MBB6" className="w-full min-h-[150px]" />}
                        {isPaypalInstagramCustomComments20 && <div id="paypal-container-KYX5M48QAQUT6" className="w-full min-h-[150px]" />}
                        {isPaypalInstagramCustomComments40 && <div id="paypal-container-8Q7MYU8WJT3M4" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookCustomComments10 && <div id="paypal-container-3YCH6G8HNK2ZN" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookCustomComments20 && <div id="paypal-container-29FEQCTQ55DH6" className="w-full min-h-[150px]" />}
                        {isPaypalFacebookCustomComments40 && <div id="paypal-container-PGH42KE9KHHH6" className="w-full min-h-[150px]" />}
                      </motion.div>
                    </div>
                  ) : isFreeTrial && isPaypalViewers1K && showFreeSubmission ? (
                    <motion.button 
                      initial={{ opacity: 0, scale: 0.9, y: 10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      onClick={handleNext}
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-500 text-white p-5 sm:p-6 rounded-2xl font-black text-xl sm:text-2xl transition-all shadow-[0_0_30px_rgba(37,99,235,0.4)] flex items-center justify-center gap-3 animate-pulse disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <span>{lang === 'de' ? 'JETZT KOSTENLOS ABSENDEN' : lang === 'ar' ? 'أرسل الآن مجانًا' : 'SEND FOR FREE NOW'}</span>
                      )}
                    </motion.button>
                  ) : (
                    <button 
                      onClick={handleNext}
                      disabled={isSubmitting}
                      className="w-full bg-blue-600 hover:bg-blue-500 text-white p-5 sm:p-6 rounded-2xl font-bold flex items-center justify-center gap-4 transition-all hover:scale-[1.02] active:scale-[0.98] shadow-xl group disabled:opacity-50"
                    >
                      {isSubmitting ? (
                        <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <>
                          <div className="flex items-center gap-2">
                            <CreditCard size={24} />
                            <span className="text-2xl">P</span>
                          </div>
                          <span className="text-lg sm:text-xl uppercase tracking-wider">{(t.modals.funnel as any).payment?.paypalCard}</span>
                        </>
                      )}
                    </button>
                  )}
                  
                  <div className="flex items-center justify-center gap-2 text-white/30 text-xs font-bold uppercase tracking-[0.2em] pt-0">
                    <Shield size={14} />
                    <span>Secure Checkout</span>
                  </div>
                </div>
              </motion.div>
            )}

            {step === 5 && (
              <motion.div 
                initial={{ opacity: 0, scale: 0.9, y: 20 }} 
                animate={{ opacity: 1, scale: 1, y: 0 }}
                className="text-center py-6 sm:py-12"
              >
                <div className="w-20 h-20 sm:w-24 sm:h-24 bg-green-500/20 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
                  <CheckCircle2 size={48} />
                </div>
                <h2 className="text-3xl sm:text-4xl font-black mb-4">{t.modals.funnel.success.thanks}</h2>
                <p className="text-lg sm:text-xl text-white/60 mb-8 max-w-md mx-auto">
                  {t.modals.funnel.success.message}
                </p>

                {/* Tracking Code Display */}
                <motion.div 
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.3 }}
                  className="bg-white/5 border border-white/10 rounded-3xl p-6 sm:p-8 mb-10 max-w-sm mx-auto backdrop-blur-md relative group overflow-hidden"
                >
                   {/* RGB LED Border for the code */}
                   <div className="absolute inset-0 z-0 pointer-events-none rounded-3xl overflow-hidden p-[2px]">
                    <motion.div
                      animate={{ rotate: 360 }}
                      transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
                      className="absolute inset-[-500%] bg-[conic-gradient(from_0deg,#ff0000,#ff7f00,#ffff00,#00ff00,#00ffff,#0000ff,#8b00ff,#ff0000,#ff0000)] opacity-40"
                    />
                    <div className="absolute inset-[2px] bg-[#050505] rounded-3xl" />
                  </div>

                  <div className="relative z-10">
                    <p className="text-blue-400 font-bold uppercase tracking-[0.3em] text-[10px] sm:text-xs mb-4">
                      {lang === 'de' ? 'DEIN TRACKING-CODE' : lang === 'ar' ? 'رمز التتبع الخاص بك' : 'YOUR TRACKING CODE'}
                    </p>
                    <div className="text-4xl sm:text-6xl font-black text-white tracking-[0.2em] font-mono mb-4">
                      {trackingCode}
                    </div>
                    <p className="text-white/40 text-[10px] sm:text-xs leading-relaxed">
                      {lang === 'de' 
                        ? 'Speichere diesen Code, um deine Bestellung unten auf der Seite zu verfolgen.' 
                        : lang === 'ar' 
                        ? 'احفظ هذا الرمز لتتبع طلبك في أسفل الصفحة.' 
                        : 'Save this code to track your order at the bottom of the page.'}
                    </p>
                  </div>
                </motion.div>

                <button 
                  onClick={onClose}
                  className="bg-white text-black px-10 py-4 rounded-2xl font-bold hover:bg-blue-50 transition-all hover:scale-105 active:scale-95 shadow-xl"
                >
                  {t.modals.funnel.buttons.close}
                </button>
              </motion.div>
            )}
          </div>
        </motion.div>
      </motion.div>
    )}
    </AnimatePresence>
  );
};

const Footer = () => {
  const { t } = useTranslation();
  const [showEmail, setShowEmail] = useState(false);

  return (
    <footer className="py-20 px-6 border-t border-white/5">
      <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-12">
        <div className="flex flex-col items-center md:items-start gap-4">
          <div className="flex flex-col items-center md:items-start gap-4 no-rtl">
            <img 
              src="https://i.postimg.cc/zf1RDhgw/56868A94-6A20-494A-A652-3F264D8C20B5-removebg-preview(2).png" 
              alt="MediaMagnet Logo" 
              className="h-28 w-auto object-contain"
              referrerPolicy="no-referrer"
            />
            <span className="font-bold text-xl tracking-tight">MediaMagnet</span>
          </div>
          <p className="text-white/30 text-sm">{t.footer.rights}</p>
        </div>
        

        <div className="flex flex-col items-center md:items-end gap-3 overflow-visible pr-5">
          <div className="flex flex-col md:flex-row-reverse items-center justify-end gap-[10px] overflow-visible">
            <button 
              onClick={() => setShowEmail(!showEmail)}
              className="w-12 h-12 glass rounded-xl flex items-center justify-center hover:bg-white/10 transition-all group relative overflow-visible"
            >
              <Mail size={24} className={`${showEmail ? 'text-blue-500 animate-pulse' : 'group-hover:text-blue-500'} transition-colors`} />
            </button>
            <AnimatePresence>
              {showEmail && (
                <motion.div
                  initial={{ opacity: 0, y: -10, scale: 0.95 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -10, scale: 0.95 }}
                  className="px-4 py-2 liquid-glass rounded-xl text-xs sm:text-sm font-black whitespace-nowrap text-blue-400 border border-blue-500/30"
                >
                  mediamagnetsupport@gmail.com
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default function App() {
  const [currentPath, setCurrentPath] = useState(window.location.pathname);

  useEffect(() => {
    const handleLocationChange = () => {
      setCurrentPath(window.location.pathname);
    };
    
    window.addEventListener('popstate', handleLocationChange);
    
    // Micro-interval check to ensure fallback detection on instant push/replaceState events
    const interval = setInterval(() => {
      if (window.location.pathname !== currentPath) {
        setCurrentPath(window.location.pathname);
      }
    }, 500);

    return () => {
      window.removeEventListener('popstate', handleLocationChange);
      clearInterval(interval);
    };
  }, [currentPath]);

  const [isFunnelOpen, setIsFunnelOpen] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState<string | undefined>();
  const [isInfoOpen, setIsInfoOpen] = useState(false);
  const [lang, setLang] = useState<Language>('de');
  const [isFreeTrial, setIsFreeTrial] = useState(false);
  const [overrideStep, setOverrideStep] = useState<number | undefined>();
  const [externalTrackingCode, setExternalTrackingCode] = useState<string>("");

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  useEffect(() => {
    const isSuccessRoute = window.location.pathname === "/success" || window.location.pathname.startsWith("/success/");
    const params = new URLSearchParams(window.location.search);
    const hasSuccessParam = params.get("payment") === "success";

    if (isSuccessRoute || hasSuccessParam) {
      const code = params.get("code") || params.get("trackingCode") || params.get("tracking") || "";
      
      // If code is empty, generate a clean 6-digit fallback
      let finalCode = code;
      if (!finalCode) {
        const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
        for (let i = 0; i < 6; i++) {
          finalCode += chars.charAt(Math.floor(Math.random() * chars.length));
        }
      }
      
      setExternalTrackingCode(finalCode);
      setOverrideStep(5);
      setIsFunnelOpen(true);
      
      // Navigate/update path silently back to home "/" so we don't display a separate page
      window.history.replaceState({}, document.title, "/" + window.location.hash);
      setCurrentPath("/");
    }
  }, [currentPath]);

  const t = translations[lang];

  const handleStartFunnel = (platform?: string, freeTrial = false) => {
    setOverrideStep(undefined);
    setExternalTrackingCode("");
    setSelectedPlatform(platform);
    setIsFreeTrial(freeTrial);
    setIsFunnelOpen(true);
  };

  const handleCloseFunnel = () => {
    setIsFunnelOpen(false);
    setOverrideStep(undefined);
    setExternalTrackingCode("");
  };

  return (
    <LanguageContext.Provider value={{ lang, setLang, t }}>
      <div className={`min-h-screen w-full max-w-full overflow-x-hidden selection:bg-blue-500/30 ${lang === 'ar' ? 'is-arabic' : ''}`}>
        <Navbar />
        
        <main>
          <Hero 
            onStartFunnel={() => handleStartFunnel(undefined, true)} 
            onShowInfo={() => setIsInfoOpen(true)}
          />
          <Platforms onStartFunnel={handleStartFunnel} />
          <OrderTracker />
        </main>

        <Footer />

        <FunnelModal 
          isOpen={isFunnelOpen} 
          onClose={handleCloseFunnel} 
          initialPlatform={selectedPlatform}
          isFreeTrial={isFreeTrial}
          overrideStep={overrideStep}
          externalTrackingCode={externalTrackingCode}
        />

        <InfoModal
          isOpen={isInfoOpen}
          onClose={() => setIsInfoOpen(false)}
        />
      </div>
    </LanguageContext.Provider>
  );
}
